import { create } from 'zustand';
import type {
  ConsoleSettings,
  Conversation,
  Message,
  SessionState,
  SlaUrgency,
  ConsultationNotification,
  BotConsultationFollowupNotification,
  ProactiveGuidanceDeliveryFailedNotification,
  MessageNotification,
  SlaWarningNotification,
  SessionCreatedNotification,
  SessionStateChangedNotification,
  SessionClosedNotification,
  SessionCategoryChangedNotification,
  SessionHiddenNotification,
  SessionRestoredNotification,
  CustomerDisplayNameUpdatedNotification,
  OutboundMessageNotification,
  OutboundDeliveryFailedNotification,
  ConversationStateDef,
  EmailDraftNotification,
  EmailDraftResolvedNotification,
  PendingEmailDraft,
  PendingEmailDraftDto,
  EscalationItemDto,
  TimelineEntry,
  TimelineEntryNotification,
  FolderId,
  TagDef,
  SessionOwnershipChangedNotification,
  SessionWithdrawnNotification,
  SessionSharedNotification,
  SessionTagsChangedNotification,
  SessionSubjectChangedNotification,
  ConsultationProposedRepliesNotification,
  ConversationPriority,
} from '@/types';
import { mergeTimelineEntries, timelineEntryFromNotification } from '@/utils/timeline';
import { isNewer, latestReasonOf, reasonOfEntry, type ConsultationReasonMark } from '@/utils/consultationReason';
import { normaliseProposedReplies } from '@/utils/storedMessage';
import { useAuthStore } from '@/stores/authStore';
import { DEFAULT_LIFECYCLE_TAB, type LifecycleTab } from '@/console/lifecycle';

/** Key of a note's proposed replies in `pendingProposedReplies` / `sentProposedReplies`. */
export function proposedReplyKey(sessionId: string, noteMessageId: string): string {
  return `${sessionId}|${noteMessageId}`;
}

/** Held early replies are bounded: a push for a note this console never loads must not accumulate for a whole shift. */
const MAX_PENDING_PROPOSED = 50;

/**
 * The consultation push and its timeline entry carry the same `ConsultationRequested.Timestamp`, but the routed copy can be
 * stamped with the ask time instead; a minute absorbs that and any clock skew between services.
 */
const REASON_SKEW_MS = 60_000;

/**
 * 2026-10-28 - set a row's consultation reason from a timeline mark, only when the mark is NEWER than what the row holds
 * (an older page or a late push must not roll it back). Returns the same array when nothing changed.
 */
function withConsultationReason(
  conversations: Conversation[],
  sessionId: string,
  mark: ConsultationReasonMark | null,
): Conversation[] {
  if (!mark) return conversations;
  const i = conversations.findIndex((c) => c.sessionId === sessionId);
  if (i < 0) return conversations;
  const row = conversations[i];
  if (row.consultationReason && !isNewer(mark, row.consultationReason)) return conversations;
  const next = [...conversations];
  next[i] = { ...row, consultationReason: { code: mark.code, at: mark.at } };
  return next;
}

/**
 * 2026-10-28 - a bot note keeps the proposed replies it already has when a later copy of the SAME note arrives without
 * them (the transcript was read before the server stored them, or a push rebuilt the card). A copy that carries replies
 * wins.
 */
function keepProposedReplies(incoming: Message, held: Message | undefined): Message {
  if (incoming.proposedReplies && incoming.proposedReplies.length > 0) return incoming;
  if (held?.proposedReplies && held.proposedReplies.length > 0) return { ...incoming, proposedReplies: held.proposedReplies };
  return incoming;
}

/**
 * Maps the backend's SessionState string (NEW / ACTIVE / CONSULTING /
 * HUMAN_CONTROLLED / CLOSED) to the UI's 4-value enum. Kept in sync with
 * the inverse mapping in utils/mapSession.ts (the former DashboardPage helper).
 */
function toUiState(backendState: string): SessionState {
  switch ((backendState ?? '').toUpperCase()) {
    case 'HUMAN_CONTROLLED':
      return 'HUMAN_TAKEOVER';
    case 'CONSULTING':
      return 'CONSULTATION_REQUESTED';
    case 'CLOSED':
      return 'CLOSED';
    default:
      return 'AI_HANDLING';
  }
}

/** The row preview is cut to about 200 characters, like the server's `lastPreview`. */
const PREVIEW_MAX = 200;
const previewText = (content: string | null | undefined): string | null => {
  const t = (content ?? '').trim();
  return t ? (t.length > PREVIEW_MAX ? t.slice(0, PREVIEW_MAX) : t) : null;
};

/**
 * ADR-033 D13 - the list's one order: `lastActivityAt` newest first, then `sessionId` descending as a stable tiebreak (the
 * same pair the server's keyset cursor walks, so a row never jumps between a loaded page and a live push). A row with no
 * `lastActivityAt` (synthesised from a push, or an old server) falls back to its last message, then its creation.
 *
 * The SLA urgency used to be the PRIMARY key here (CRITICAL rows jumped the queue). It is a badge only now: it is still
 * on the row and still rendered, it just no longer reorders anything.
 */
function activityMs(c: Conversation): number {
  const t = Date.parse(c.lastActivityAt ?? c.lastMessageTimestamp ?? c.createdAt ?? '');
  return Number.isFinite(t) ? t : 0;
}

export function sortByActivity(conversations: Conversation[]): Conversation[] {
  return [...conversations].sort((a, b) => {
    const diff = activityMs(b) - activityMs(a);
    if (diff !== 0) return diff;
    return a.sessionId < b.sessionId ? 1 : a.sessionId > b.sessionId ? -1 : 0;
  });
}

/** The later of two ISO instants (an unparseable or absent one never wins). Keeps activity bumps monotonic. */
function laterIso(current: string | null | undefined, incoming: string): string {
  if (!current) return incoming;
  const a = Date.parse(current);
  const b = Date.parse(incoming);
  if (!Number.isFinite(b)) return current;
  if (!Number.isFinite(a)) return incoming;
  return b > a ? incoming : current;
}

/**
 * ADR-033 - claims this console has sent and not yet heard the outcome of (guidance, takeover, draft approve/edit).
 *
 * `ReceiveSessionWithdrawn` means "someone claimed a conversation you may be showing". When it lands during our own claim
 * it is ambiguous - the claimer may be a teammate who beat us, or it may be OUR claim echoing to another tab - and the
 * console does not decide which: it VERIFIES (re-reads the row). Module scope rather than store state because it is not
 * reactive data, and expiring because a claim whose outcome never arrives must not keep a row out of the hidden set for
 * ever. Cleared by the ownership push that settles it, and by `resetAll`.
 */
const PENDING_CLAIM_TTL_MS = 30_000;
const pendingClaims = new Map<string, number>();

/** Record that this console just sent a claim (guidance / takeover / draft approve|edit) for `sessionId`. */
export function markPendingClaim(sessionId: string, now: number = Date.now()): void {
  if (sessionId) pendingClaims.set(sessionId, now);
}

/** Forget a pending claim (its outcome is known, or the send was rejected). */
export function clearPendingClaim(sessionId: string): void {
  pendingClaims.delete(sessionId);
}

/** Is a claim from this console for `sessionId` still within its 30 s window? */
export function hasPendingClaim(sessionId: string, now: number = Date.now()): boolean {
  const at = pendingClaims.get(sessionId);
  if (at === undefined) return false;
  if (now - at > PENDING_CLAIM_TTL_MS) {
    pendingClaims.delete(sessionId);
    return false;
  }
  return true;
}

/**
 * ADR-033 / PR-C2 - an ORDER between a row re-read and the pushes that move the row, which timestamps cannot give (two
 * events in one millisecond have no order; the hidden set's `Date.now()` map has exactly that blind spot).
 *
 * Every push that moves what a re-read also carries - the lifecycle state, who owns it, its consultation, its category,
 * its name - stamps the row with the next value of ONE counter. A re-read takes the counter's value when it is SENT
 * (`currentPushClock`). When its answer lands, a row stamped AFTER that moment was changed by a push the answer cannot
 * have seen, so the answer is refused rather than rolling the push back (a late answer must never resurrect a release
 * or reopen a closed conversation). Module scope, not store state: it is not reactive data, and it is wiped by `resetAll`.
 * Message pushes deliberately do NOT stamp: they move the preview, which the activity merge already protects, and a busy
 * chat must not make every re-read of its row stale.
 */
let pushClock = 0;
const rowPushedAt = new Map<string, number>();
const touchRow = (sessionId: string): void => {
  if (sessionId) rowPushedAt.set(sessionId, ++pushClock);
};

/** The push clock now: a re-read records it at the moment it is sent. */
export function currentPushClock(): number {
  return pushClock;
}

/** Who this console is, as the caller (the hub hook) reads it from the token. Never taken from a push. */
export interface OwnershipContext {
  /** This console's own staff id; null/empty = unknown, which fails closed (nothing is "mine"). */
  me: string | null | undefined;
  /** Holds `workspace.viewAll`: may READ others' conversations (never acts on them). */
  viewAll: boolean;
}

/** What the caller must do after `handleSessionWithdrawn`. */
export type WithdrawOutcome = 'hide' | 'verify' | 'ignore';

/** One folder's paging state. A row may be in several folders; the row cache (`conversations`) is shared. */
export interface FolderPageState {
  /** Row ids in the order the server listed them (keyset order). */
  ids: string[];
  /** The server's opaque cursor for the NEXT page; null = none. */
  cursor: string | null;
  hasMore: boolean;
  loading: boolean;
  /** The last request for this folder failed. Cleared by the next `setFolderLoading(true)` or a page. */
  error: boolean;
  /** Epoch ms of the last page applied (null = never loaded). */
  loadedAt: number | null;
}

const emptyFolderPage = (): FolderPageState => ({
  ids: [],
  cursor: null,
  hasMore: false,
  loading: false,
  error: false,
  loadedAt: null,
});

/**
 * The ONE gate every row-creating or row-decorating door consults. A conversation is blocked when it is a spam tombstone
 * (OP-1005, until an Undo) OR in the reversible hidden set (ADR-033: withdrawn / 404 / owned by someone else; lifted by a
 * later share, an own claim, or a server page that listed it after the hide).
 */
function isBlocked(
  state: Pick<ConversationState, 'spamTombstones' | 'hiddenSessions'>,
  sessionId: string,
): boolean {
  return !!state.spamTombstones[sessionId] || sessionId in state.hiddenSessions;
}

/** The separator the escalation queue puts between the asks of one open item (EscalationService.MergeConsultationContext). */
export const ASKED_AGAIN_SEPARATOR = '--- Asked again ---';

/**
 * OP-1516 (live 2026-10-27) — the ONE consultation-thread entry an open queue item stands for: its latest ask, alone, at the
 * time it was made. A re-ask used to be rebuilt from the item's MERGED context under the re-ask's messageId, stamped at the
 * item's creation, so it rendered as a second bubble at the FIRST ask's time repeating the first ask.
 *
 * An item from an older server (no `latestAskContext`) whose context merges several asks has no trustworthy time for the
 * newest one, so it yields no entry at all: the persisted transcript holds every ask as its own row at its own time.
 */
export function consultationAskOf(item: EscalationItemDto): { content: string; at: string } | null {
  const latest = (item.latestAskContext ?? '').trim();
  if (latest.length > 0) return { content: latest, at: item.latestAskAt || item.createdAt };
  const context = (item.context ?? '').trim();
  if (context.length === 0 || context.includes(ASKED_AGAIN_SEPARATOR)) return null;
  return { content: context, at: item.createdAt };
}

/** A copy of `folderPages` with `sessionId` removed from every folder's id list (a new object only when something changed). */
function withoutSession(
  folderPages: Record<FolderId, FolderPageState>,
  sessionId: string,
): Record<FolderId, FolderPageState> {
  let changed = false;
  const next: Record<FolderId, FolderPageState> = {};
  for (const [folder, page] of Object.entries(folderPages)) {
    if (page.ids.includes(sessionId)) {
      changed = true;
      next[folder] = { ...page, ids: page.ids.filter((id) => id !== sessionId) };
    } else {
      next[folder] = page;
    }
  }
  return changed ? next : folderPages;
}

/**
 * Merge a row the SERVER listed into the shared row cache. The incoming row wins, except for what a list row cannot
 * know or must not roll back: the console-only `pendingDraft` and `unreadCount`, and anything a push has already moved
 * FORWARD (a newer `ownerVersion`, a newer `tagsVersion`, a later `lastActivityAt`) - a page requested before a push and
 * answered after it must not undo the push.
 */
function mergeListedRow(prev: Conversation | undefined, next: Conversation): Conversation {
  if (!prev) return next;
  let out: Conversation = next;
  if (prev.pendingDraft && !next.pendingDraft) out = { ...out, pendingDraft: prev.pendingDraft };
  if (prev.unreadCount && !next.unreadCount) out = { ...out, unreadCount: prev.unreadCount };
  // The open consultation's console-only decoration (who it is routed to, its transaction) never comes on a list row:
  // it arrives on the consultation push or the escalation-queue feed. A page or a single-row read that still says the
  // conversation is CONSULTING must not wipe it - without this a reload turned an actionable consultation read-only.
  if (prev.state === 'CONSULTATION_REQUESTED' && out.state === 'CONSULTATION_REQUESTED') {
    if (out.consultationBotId === undefined && prev.consultationBotId !== undefined) {
      out = { ...out, consultationBotId: prev.consultationBotId };
    }
    if (out.assignedToOther === undefined && prev.assignedToOther !== undefined) {
      out = { ...out, assignedToOther: prev.assignedToOther };
    }
    if (out.consultationTransactionId === undefined && prev.consultationTransactionId !== undefined) {
      out = { ...out, consultationTransactionId: prev.consultationTransactionId };
    }
    // 2026-10-28 - the reason ("asked for a person") is console-only too: learned from the timeline, never on a list row.
    if (out.consultationReason === undefined && prev.consultationReason !== undefined) {
      out = { ...out, consultationReason: prev.consultationReason };
    }
  }
  if ((prev.ownerVersion ?? 0) > (out.ownerVersion ?? 0)) {
    out = {
      ...out,
      ownerAgentId: prev.ownerAgentId,
      ownerSince: prev.ownerSince,
      ownerSource: prev.ownerSource,
      ownerReleased: prev.ownerReleased,
      ownerVersion: prev.ownerVersion,
    };
  }
  if ((prev.tagsVersion ?? 0) > (out.tagsVersion ?? 0)) {
    out = { ...out, tags: prev.tags, tagsVersion: prev.tagsVersion };
  }
  if (activityMs(prev) > activityMs(out)) {
    out = {
      ...out,
      lastActivityAt: prev.lastActivityAt,
      lastCustomerMessageAt: prev.lastCustomerMessageAt,
      lastOutboundAt: prev.lastOutboundAt,
      lastMessageContent: prev.lastMessageContent,
      lastMessageTimestamp: prev.lastMessageTimestamp,
    };
  }
  return out;
}

/**
 * 2026-07-23 — safety-timeout backstop for `botThinking`. The indicator is
 * cleared as soon as an outbound (bot/agent) reply arrives or the session
 * leaves AI_HANDLING, but if that clear event is ever missed the dots must
 * not spin forever — module-scope (not store state) since a setTimeout
 * handle isn't itself reactive data.
 */
const BOT_THINKING_SAFETY_TIMEOUT_MS = 45_000;
const botThinkingTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * HC2.2 / OP-061 — maps the backend's short failure `reason` code into the
 * operator-facing alert line. Unknown codes fall back to a generic-but-clear
 * message so a new backend code never renders as a raw token.
 */
export function proactiveGuidanceFailureMessage(reason: string): string {
  switch ((reason ?? '').toLowerCase()) {
    case 'compose_failed':
      return "The bot couldn't deliver your guidance to the customer.";
    case 'compose_failed_with_followup':
      return "The bot couldn't deliver your guidance to the customer and has asked you a clarifying question.";
    case 'content_filter_rejected':
      return "The content filter blocked the bot's message; rephrase or take over.";
    default:
      return "The bot couldn't deliver your guidance to the customer.";
  }
}

export interface ConversationState {
  /**
   * The shared ROW CACHE: every conversation any folder, push or deep link has loaded, keyed by `sessionId`, sorted by
   * `lastActivityAt` (newest first). A row may belong to several folders; `folderPages[*].ids` say which.
   */
  conversations: Conversation[];
  /** Currently selected conversation sessionId. */
  activeSessionId: string | null;
  /** Messages for the active conversation. */
  messages: Message[];

  /**
   * 2026-07-23 — per-session "the bot is actively composing a reply" flag,
   * keyed by sessionId. Drives the CustomerChatSurface dots indicator, which
   * previously rendered unconditionally for the whole lifetime of any
   * bot-handled conversation instead of only while awaiting a reply. Set
   * true when a customer message arrives (handleMessageNotification);
   * cleared when the bot's/agent's reply lands (handleOutboundMessage), the
   * session leaves AI_HANDLING into HUMAN_TAKEOVER, or it closes — plus a
   * bounded safety timeout in case a clear event is ever missed.
   */
  botThinking: Record<string, boolean>;

  /** Set or clear the "bot is composing" flag for a session (see `botThinking`). */
  setBotThinking: (sessionId: string, thinking: boolean) => void;

  // ─── ADR-033 / CONTRACT-C: folder paging, hidden set, ownership, tags ────────────────────────────

  /**
   * Per-folder keyset paging (D13), keyed by the server's folder id (`mine`, `cat:<key>`, `tag:<key>`, `search:<q>`...).
   * Each folder carries its OWN cursor, so loading more of one never moves another.
   */
  folderPages: Record<FolderId, FolderPageState>;

  /** `GET /api/sessions/counts?groupBy=folder` - server counts per folder id. Drafts is NOT here (it is the drafts feed). */
  folderCounts: Record<FolderId, number>;

  /** False until the first counts answer, so the rail can show a dash instead of a false zero. */
  countsLoaded: boolean;

  /**
   * The list's lifecycle tab (All / Need help / Bot handling / Agent handling). Sent to the server as `lifecycle` on the
   * list and the counts, and part of every folder page's cache key (`folderPageKey`), so switching tabs never shows rows
   * or a cursor from another tab. The persisted per-viewer choice is written by the list, read by the loaders at boot.
   */
  lifecycleTab: LifecycleTab;
  setLifecycleTab: (tab: LifecycleTab) => void;
  /**
   * How many open conversations need help (the rail's consultation badge). The counts endpoint no longer reports a
   * `need-consult` folder, so the loaders ask for `lifecycle=need-help` and keep the answer here. null = not known yet.
   */
  needHelpCount: number | null;
  setNeedHelpCount: (n: number | null) => void;

  /** The tenant's tag catalog as the console reads it (active and inactive, so a chip on an old row still resolves). */
  tagCatalog: TagDef[];

  /** Bumped by `ReceiveTagCatalogChanged`; the shell watches it and refetches the catalog. */
  catalogVersion: number;

  /**
   * PR-A - the tenant's console settings as the SERVER last answered them (GET /api/sessions/console-settings), or null
   * when not (yet) known: before the first answer, and after a failed read. Null renders the default DISPLAY (tag folders
   * shown). The console reads display preferences from this and nothing else: who may see which conversation is the
   * server's answer on every list, never a value inferred from here.
   */
  consoleSettings: ConsoleSettings | null;

  /** Bumped by `ReceiveConsoleConfigChanged`; the shell watches it and refetches the console settings AND the state defs. */
  consoleConfigVersion: number;

  /** The current free-text search (`q`), '' = none. */
  searchQuery: string;

  /**
   * ADR-033 - the REVERSIBLE hidden set: sessionId -> the epoch ms it was hidden. Conversations this console must not
   * show right now: withdrawn from it, owned by someone else, or a 404 on a row it thought it could see. In memory only,
   * never persisted, per tenant+agent (cleared on logout or a change of either), and DISTINCT from `spamTombstones` -
   * a tombstone is for good (until Undo), this is lifted by a share, an own claim, or a server page listing the row
   * AFTER the hide. The timestamp is what lets an in-flight page that was requested before the hide be told apart from
   * one requested after it.
   */
  hiddenSessions: Record<string, number>;

  /** The conversation the agent had open when it was hidden, so the page can say "this conversation is no longer available". */
  lastWithdrawnId: string | null;

  /** Is the conversation in the hidden set (NOT the spam tombstones). */
  isHidden: (sessionId: string) => boolean;

  /**
   * Hide a conversation: add it to the hidden set, drop it from every folder and from the row cache, drop its Bot
   * Consultation log, timeline and "composing" flag, and deselect it if open (setting `lastWithdrawnId`). Idempotent.
   * Never creates a spam tombstone.
   */
  hideSession: (sessionId: string) => void;

  /** Lift a hide. Removes the hidden-set entry and NOTHING else - never a spam tombstone, never re-adds the row. */
  unhideSession: (sessionId: string) => void;

  /**
   * Apply one keyset page to a folder.
   *  - rows that are spam-tombstoned are dropped;
   *  - a hidden row is dropped when the page was requested BEFORE it was hidden (an in-flight page cannot resurrect it),
   *    and un-hidden when the page was requested AT OR AFTER the hide (the server listed it later, so the server wins);
   *  - the rest are merged into the shared row cache (`pendingDraft`, `unreadCount` and anything a push moved forward
   *    are kept) and their ids recorded, de-duplicated (`append` keeps the first occurrence).
   * @param requestedAt epoch ms the request was SENT, not when it answered.
   */
  applyFolderPage: (
    folder: FolderId,
    rows: Conversation[],
    nextCursor: string | null,
    hasMore: boolean,
    requestedAt: number,
    mode: 'replace' | 'append',
  ) => void;

  /**
   * Apply ONE row the server just re-read (`GET /api/sessions/{id}`, 200) to the row cache - the single-row twin of
   * `applyFolderPage`, with the same gate:
   *  - a spam-tombstoned row is refused;
   *  - a row hidden AFTER the read was requested is refused (a withdraw push that landed mid-flight outranks an answer
   *    that predates it) and stays hidden;
   *  - otherwise the hide (if any) is lifted - the server, asked after the hide, still shows the row - and the row is
   *    merged exactly like a listed one (console-only `pendingDraft` / `unreadCount` / consultation fields kept,
   *    anything a push moved forward kept), inserted if it was not held, and no longer `partial`.
   *  - a row that a state / owner / consultation / category / name PUSH moved after the read was sent (`sentAtClock` is
   *    `currentPushClock()` taken when it was sent) is `stale`: the answer cannot have seen that push and must not roll it
   *    back. Without `sentAtClock` this check is skipped.
   * Returns whether the row is now held. @param requestedAt epoch ms the read was SENT, not when it answered.
   */
  applyRefreshedRow: (row: Conversation, requestedAt: number, sentAtClock?: number) => 'applied' | 'hidden' | 'stale';

  /** Mark a folder as loading (or not). Starting a load clears its error flag. */
  setFolderLoading: (folder: FolderId, loading: boolean) => void;

  /** Mark a folder's last request as failed (and no longer loading). */
  setFolderError: (folder: FolderId) => void;

  /** Forget every folder's paging state and the row cache, KEEPING the open conversation's row (OP-372). The hidden set stays. */
  resetFolderPages: () => void;

  /** Replace the folder counts (and mark them loaded). */
  setFolderCounts: (counts: Record<FolderId, number>) => void;

  /** Replace the tag catalog. */
  setTagCatalog: (tags: TagDef[]) => void;

  /** Replace the console settings with the server's answer (null = not known: a failed read). */
  setConsoleSettings: (settings: ConsoleSettings | null) => void;

  /** Set the free-text search query (the TEXT in the top bar's box; the search VIEW is the `search:<q>` folder). */
  setSearchQuery: (q: string) => void;

  /**
   * Forget every `search:<q>` folder page. A search is a snapshot of what the agent typed: leaving the search view drops
   * it, so the same words typed again later ask the server again instead of showing an old answer. The rows themselves
   * stay in the row cache (the cache is shared with every folder).
   */
  forgetSearchPages: () => void;

  /**
   * Forget everything this console knew about the previous tenant+agent: rows, folders, counts, catalog, the hidden
   * set, spam tombstones, pending claims, the open conversation. Called when the signed-in user goes away or changes.
   */
  resetAll: () => void;

  /**
   * `ReceiveSessionOwnershipChanged`. Ignored when `ownerVersion` is OLDER than the row's (a late push must not undo a
   * newer claim). Hides the row when somebody else now owns it, this console is not `viewAll` AND the held row says
   * private ownership is in effect (`ownershipEnforced === true` - defence in depth; the server already stopped
   * delivering it). Where it is not in effect (or the row is not loaded) the owner is only recorded and the row stays.
   * Un-hides it, and settles any pending claim, when the owner is this console or nobody. The console never decides
   * ownership: it records what the server said, and the caller re-reads the row.
   */
  handleSessionOwnershipChanged: (
    notification: SessionOwnershipChangedNotification,
    ctx: OwnershipContext,
  ) => void;

  /**
   * `ReceiveSessionWithdrawn` - someone claimed a conversation this console may be showing.
   *  - `'ignore'` for a `viewAll` viewer (it may keep reading);
   *  - `'verify'` when the row is MINE or a claim from this console is pending (ambiguous: the caller must re-read the row),
   *    and when the held row does not say private ownership is in effect (`ownershipEnforced !== true`) or is not loaded:
   *    the legacy shared mode shows everyone every conversation, so nothing is hidden on a push alone - the re-read is the
   *    server's word, and a 404 hides it then;
   *  - `'hide'` otherwise (the held row says ownership is enforced and it is not mine: hidden here and now).
   */
  handleSessionWithdrawn: (notification: SessionWithdrawnNotification, ctx: OwnershipContext) => WithdrawOutcome;

  /**
   * `ReceiveSessionShared` - an owner was cleared, the conversation is team-shared again. Un-hides it and marks the row's
   * owner UNKNOWN (`ownerAgentId` undefined - NOT null: this push is content-free and unversioned, so a late one must not
   * make a freshly claimed row look team-shared; every consumer fails closed on an unknown owner); the caller then
   * re-reads the row, and that answer sets the truth. Never lifts a spam tombstone.
   */
  handleSessionShared: (notification: SessionSharedNotification) => void;

  /** `ReceiveSessionTagsChanged` - apply tags only when `tagsVersion` is NEWER than the row's. */
  handleSessionTagsChanged: (notification: SessionTagsChangedNotification) => void;

  /** Apply a tag post-image (the answer of attach/detach) under the same newer-version rule as the push. */
  setConversationTags: (sessionId: string, tags: string[], tagsVersion: number) => void;

  /** `ReceiveTagCatalogChanged` - bump `catalogVersion` so the shell refetches the catalog. */
  handleTagCatalogChanged: () => void;

  /** `ReceiveConsoleConfigChanged` - bump `consoleConfigVersion` so the shell refetches the console settings and state defs. */
  handleConsoleConfigChanged: () => void;

  /** `ReceiveSessionSubjectChanged` - patch the subject (null = cleared). */
  handleSessionSubjectChanged: (notification: SessionSubjectChangedNotification) => void;

  /**
   * OP-1005 - the TOMBSTONES of conversations marked as spam / unwanted, keyed by sessionId.
   *
   * Hiding a row is not enough on its own: the console has half a dozen independent ways a session
   * can come back - the session-list reload (an in-flight GET that answered before the mark landed),
   * a late consultation push, the email-draft push and its rehydrate, the consultation rehydrate, a
   * new-message push that synthesises a row, a session-created push. A tombstone is the single fact
   * every one of those entry points consults, so none of them can resurrect a hidden conversation
   * for the rest of this browser session. In-memory on purpose: a page reload asks the server, which
   * no longer lists a marked session.
   *
   * Cleared ONLY by an Undo (the spam view) or `ReceiveSessionRestored`.
   */
  spamTombstones: Record<string, true>;

  /**
   * OP-1005 - hide a conversation for good: tombstone it, drop its row, its transcript, its Bot
   * Consultation log and its "bot is composing" flag, and deselect it if it was open. Shared by this
   * agent's own successful mark and by `ReceiveSessionHidden` (another agent marked it), so the two
   * cannot drift apart. Idempotent.
   */
  markSpamHidden: (sessionId: string) => void;

  /** OP-1005 - lift a tombstone (Undo, or `ReceiveSessionRestored`). Does NOT reload the list - the caller does. */
  clearSpamTombstone: (sessionId: string) => void;

  /** OP-1005 - `ReceiveSessionHidden`: another agent (or this one) marked the conversation as spam. */
  handleSessionHidden: (notification: SessionHiddenNotification) => void;

  /** OP-1005 - `ReceiveSessionRestored`: someone undid a spam mark. Lifts the tombstone; the hub hook then reloads the list. */
  handleSessionRestored: (notification: SessionRestoredNotification) => void;

  /**
   * ADR-017 D3 — the tenant's conversation-state definitions (label/color/order
   * per category key), fetched once on load. Drives the ConversationList
   * directory section headers. Empty until fetched (the list then groups every
   * row under the trailing "Uncategorized" fallback section).
   */
  stateDefs: ConversationStateDef[];

  /**
   * Per-session "Bot Consultation" transcript. Keyed by sessionId. Holds:
   *   - The bot's "I need help" status report (synthetic AI message
   *     pushed when ConsultationRequested arrives — content = the rich
   *     context the bot built describing what the customer asked, what
   *     it retrieved, and its confidence).
   *   - The agent's outgoing guidance (optimistic AGENT message pushed
   *     by ConversationPanel.handleSendGuidance).
   *   - The bot's relayed reply after the agent advised (synthetic AI
   *     message — same content as the customer-facing reply, mirrored
   *     into the consultation log).
   *
   * Lives in the store rather than ConversationPanel's local state so
   * SignalR-driven side-channel events (consultation start, dismissal)
   * can push into it without re-mounting the panel.
   */
  consultationLog: Record<string, Message[]>;

  /**
   * Replace the full conversations list (e.g., initial load from API).
   *
   * PRESERVES each surviving row's `pendingDraft` (ADR-019): a draft is console-only
   * state that the session list knows nothing about, so replacing rows wholesale used
   * to silently drop the review card on every refetch — including the hub-reconnect
   * refetch, which fires on any transient disconnect. The incoming row wins for every
   * other field.
   */
  setConversations: (conversations: Conversation[]) => void;

  /** Replace the conversation-state definitions (ADR-017 D3, fetched on load). */
  setStateDefs: (defs: ConversationStateDef[]) => void;

  /** Add or update a single conversation from a SignalR event. */
  upsertConversation: (conversation: Conversation) => void;

  /** Remove a conversation (e.g., session closed). */
  removeConversation: (sessionId: string) => void;

  /** Select a conversation to view its messages. */
  setActiveSession: (sessionId: string | null) => void;

  /** Replace messages for the active conversation. */
  setMessages: (messages: Message[]) => void;

  /** Append a new message from SignalR. */
  addMessage: (message: Message) => void;

  /**
   * Append a message to a session's Bot Consultation transcript.
   *
   * OP-1516 — `authoritative` marks a row read from the persisted transcript (the system of record: one row per ask, its
   * own text, its own time). It REPLACES an entry already held under the same messageId (or the same bot note), so a card
   * rebuilt from the queue or a push can never hide the real row's time and text.
   */
  appendConsultationMessage: (sessionId: string, message: Message, opts?: { authoritative?: boolean }) => void;

  /** Clear a session's Bot Consultation transcript (e.g., on dismissal / takeover). */
  clearConsultationMessages: (sessionId: string) => void;

  /**
   * 2026-10-28 - proposed replies that arrived (`ReceiveConsultationProposedReplies`) before their bot note was in the
   * consultation log, keyed `{sessionId}|{noteMessageId}`. Applied the moment that note is appended, then dropped.
   */
  pendingProposedReplies: Record<string, string[]>;

  /**
   * 2026-10-28 - which proposed reply of a note this console SENT successfully, keyed `{sessionId}|{noteMessageId}`.
   * Survives a transcript reload (the note is replaced, this is not). Only a successful invoke records it.
   */
  sentProposedReplies: Record<string, number>;

  /** `ReceiveConsultationProposedReplies` - attach the replies to the note already held, or hold them until it lands. */
  handleConsultationProposedReplies: (notification: ConsultationProposedRepliesNotification) => void;

  /** Record that reply `index` of note `noteMessageId` was sent (after the hub accepted it). */
  markProposedReplySent: (sessionId: string, noteMessageId: string, index: number) => void;

  /** Handle a consultation request notification. */
  handleConsultation: (notification: ConsultationNotification) => void;

  /**
   * HC1.D (2026-05-13) — handle a bot-consultation-followup notification.
   * The bot is asking the teammate a clarifying question (because their
   * earlier guidance was too vague for the bot to act on). Append it to
   * the existing Bot Consultation pane as another bot-side message so
   * the teammate sees it in the same place they saw the original
   * consultation context.
   */
  handleBotConsultationFollowup: (notification: BotConsultationFollowupNotification) => void;

  /**
   * ADR-019 D5/D6 — handle an `EmailReplyDraftCreated` fan-out (a bot-authored
   * Email reply gated for human review). Sets/replaces the row's
   * `pendingDraft` per the anti-clobber decision table:
   *   - Unknown sessionId ⇒ no-op (the row has nowhere to render the card;
   *     ADR-019 "Revisit: console rehydrate of in-flight drafts" is the
   *     accepted v1 gap — the next session-list load does not resurrect it).
   *   - Same `draftId`, LOWER incoming revision ⇒ ignore entirely (bus
   *     ordering is not guaranteed; a late lower revision must never clobber
   *     a newer one).
   *   - Same `draftId`, SAME revision ⇒ the routed dual-copy case (mirrors
   *     `handleConsultation`'s ADR-016 §D8 dedupe): merge PREFERRING the
   *     actionable copy — once `assignedToOther=false` is recorded for this
   *     revision, a same-revision awareness duplicate (`true`) must never
   *     downgrade the owner to read-only, regardless of arrival order.
   *   - Same `draftId`, HIGHER revision, or a DIFFERENT `draftId` ⇒ replace
   *     unconditionally (a new/re-drafted revision supersedes the old one).
   * A fresh (non-merge) set always clears `actionRejected` — a new revision
   * means any stale "not owner" notice from a prior revision no longer
   * applies. Never touches `state` / `slaUrgency` / `lastMessageContent`
   * (a draft has not reached the customer, so it must not reorder the inbox
   * or flip a badge) and never appends to `messages` / `consultationLog`
   * (rendering an unapproved draft as a transcript row would be a lie).
   */
  handleEmailDraft: (notification: EmailDraftNotification) => void;

  /**
   * ADR-019 — merge the server's in-flight drafts (`GET /api/ai/email-drafts/pending`)
   * onto their conversation rows. This is the REHYDRATE half of the draft lifecycle:
   * `handleEmailDraft` only ever fires for a draft created while this console was
   * connected and listening, so without this a draft was invisible for its entire
   * 90-minute review window and then auto-sent unreviewed.
   *
   * Called after every session-list load (boot + hub-reconnect refetch). Idempotent, and
   * it will NOT overwrite a live-pushed card for the same draft at the same-or-newer
   * revision — the push carries per-recipient ownership and any local "not yours" notice
   * that a REST row cannot reproduce.
   *
   * @param drafts  Rows from the rehydrate endpoint. A row whose session is NOT in the loaded
   *   list gets a minimal PLACEHOLDER conversation built from the draft itself (OP-582) — it
   *   used to be silently dropped, which hid precisely the longest-waiting drafts, since the
   *   session list is one page and a held draft can outlive it.
   * @param myAgentId This console's own agent id, used to derive `assignedToOther`.
   */
  hydratePendingDrafts: (drafts: PendingEmailDraftDto[], myAgentId?: string | null) => void;

  /**
   * OP-957 (2026-09-30) — rebuild open consultation cards from the queue at console start-up.
   *
   * A consultation reached the console ONLY as a live `ReceiveConsultation` push, so a console opened (or
   * reloaded) after the bot asked showed nothing — in the Niloosoft test all 12 consultations expired
   * unseen. This marks each loaded row with an OPEN item (PENDING or ASSIGNED) as CONSULTATION_REQUESTED and
   * puts the bot's note into the Bot Consultation pane under the same messageId the live push and the
   * transcript use. Rows that are CLOSED or HUMAN_TAKEOVER are left alone, the row's last-message
   * preview is not touched, and an actionable live card is never downgraded to read-only.
   *
   * @param items     Rows from GET /api/escalation/queue.
   * @param myAgentId This console's own agent id, used to derive `assignedToOther`.
   */
  hydrateOpenConsultations: (items: EscalationItemDto[], myAgentId?: string | null) => void;

  /**
   * ADR-019 D5/D9 — handle an `EmailReplyDraftResolved` fan-out (a review
   * draft reached a terminal state, or was claimed by another agent).
   *   - Unknown sessionId, no `pendingDraft`, or a `draftId` that does not
   *     match the row's current pending draft ⇒ no-op (a resolution for a
   *     stale/other draft must never clear the current card).
   *   - `outcome === 'rejectedNotOwner'` ⇒ NON-terminal: keep `pendingDraft`
   *     and set `actionRejected: true` so the card shows a "not yours" notice
   *     instead of vanishing (ADR-019 D6/D9).
   *   - Any other outcome (`approved | edited | autoSentMaxWait |
   *     autoSentIdle | cancelled | superseded | failed`) ⇒ clear
   *     `pendingDraft` — the card must not keep offering buttons for an email
   *     that already left, including the auto-sends.
   */
  handleEmailDraftResolved: (notification: EmailDraftResolvedNotification) => void;

  /**
   * HC2.2 / OP-061 (2026-05-27) — handle a proactive-guidance-delivery-failure
   * notification. Actionable teammate guidance could not reach the customer
   * (compose failed / content filter rejected) and there was no open
   * consultation. Surfaces a VISIBLE alert: appends a synthetic role="alert"
   * entry to the session's Bot Consultation pane AND bumps the conversation
   * row's slaUrgency so it stands out in the list. Deduped by transactionId.
   */
  handleProactiveGuidanceDeliveryFailed: (
    notification: ProactiveGuidanceDeliveryFailedNotification,
  ) => void;

  /** Handle an incoming message notification. */
  handleMessageNotification: (notification: MessageNotification) => void;

  /** Handle an SLA warning notification. */
  handleSlaWarning: (notification: SlaWarningNotification) => void;

  /** Handle a SessionCreated notification — insert the new session at the top. */
  handleSessionCreated: (notification: SessionCreatedNotification) => void;

  /**
   * 2026-07-23 — handle a CustomerDisplayNameUpdated notification: patch
   * customerDisplayName onto every conversation row matching customerId (a
   * customer can have several open sessions at once). Closes the gap where a
   * session created live (via handleSessionCreated/handleMessageNotification)
   * before the customer's name resolved would show "Unidentified client" /
   * the generic guest label until the next full session-list reload.
   */
  handleCustomerDisplayNameUpdated: (notification: CustomerDisplayNameUpdatedNotification) => void;

  /** Handle a SessionStateChanged notification — flip the state badge. */
  handleSessionStateChanged: (notification: SessionStateChangedNotification) => void;

  /** Handle a SessionClosed notification — remove the session from the active list. */
  handleSessionClosed: (notification: SessionClosedNotification) => void;

  /**
   * ADR-017 D2 — handle a SessionCategoryChanged notification: update the row's
   * Category so it moves to the matching console directory section, then
   * re-sort. A pure classification change — never touches the lifecycle state.
   */
  handleSessionCategoryChanged: (notification: SessionCategoryChangedNotification) => void;

  /**
   * Patch a conversation's staff-set subject (its name). Used for the optimistic write in the
   * Conversation panel's inline editor and for the revert when the server rejects it.
   *
   * `null` CLEARS the subject, after which the UI falls back to its derived per-channel label.
   * Unlike a category change this does NOT re-sort: a subject is not an ordering input, so
   * re-sorting would move rows under the agent for a pure retitle.
   */
  setConversationSubject: (sessionId: string, subject: string | null) => void;

  /**
   * Re-classify one conversation locally (ADR-017 D2). The shared implementation behind BOTH the
   * SignalR echo and the console's own optimistic write, so a category set by this agent and one
   * set by a teammate take an identical path — including the re-sort, which matters because a
   * category change moves the row into a different inbox directory.
   *
   * Separate from `handleSessionCategoryChanged` only because that one takes a full server
   * notification; calling it locally would mean fabricating a `tenantId` and a `timestamp` we do
   * not have.
   */
  setConversationCategory: (sessionId: string, category: string | null) => void;
  /** Set one row's priority (and whether a person pinned it) from a confirmed save. Never re-sorts: priority is not the list's order. */
  setConversationPriority: (sessionId: string, priority: ConversationPriority, pinned?: boolean) => void;

  /**
   * Handle an outbound message (AI or agent reply) — append to the active
   * thread and update the conversation row's last-message preview so the
   * list and the panel both reflect the message without a page refresh.
   */
  handleOutboundMessage: (notification: OutboundMessageNotification) => void;
  /**
   * OP-681 - marks ONE message as definitely not delivered.
   *
   * Marks, never appends: if the message is not in the open thread there is nothing to show and this
   * is a no-op rather than a synthetic bubble. Only ever sets the flag to `true` - absence stays
   * "nobody has said", which is a different fact from a failure.
   */
  handleOutboundDeliveryFailed: (notification: OutboundDeliveryFailedNotification) => void;

  /**
   * Chat action timeline (staff-only) for the OPEN conversation, keyed by sessionId. Entries are held
   * oldest-first and de-duplicated on `entryId`. Only the open conversation's entries are kept live: a push
   * for any other session is dropped (opening it reloads the authoritative page), and switching the open
   * conversation prunes the rest, so a long shift never accumulates every session's history in memory.
   */
  timeline: Record<string, TimelineEntry[]>;

  /**
   * Merge a loaded page into a session's timeline (de-duplicated on `entryId`, the loaded copy wins).
   * MERGES rather than replaces: a push that landed while the page was in flight must survive it.
   */
  hydrateTimeline: (sessionId: string, entries: TimelineEntry[]) => void;

  /** `ReceiveTimelineEntry` - add one live entry to the open conversation; a no-op for any other session or a duplicate. */
  handleTimelineEntry: (notification: TimelineEntryNotification) => void;

  /**
   * 2026-06-10 conversation-close design — optimistic local update after the
   * agent explicitly closes a session via POST /api/sessions/{id}/close. Sets
   * the conversation's state to CLOSED in the store immediately; the hub's
   * SessionClosed notification confirms (or removes) it shortly after.
   */
  markClosed: (sessionId: string) => void;
}

/**
 * ADR-017 D3 — the category a brand-new / uncategorised session shows under: the
 * tenant's active `isDefault` state-def key, or null when the defs aren't loaded
 * (or none is marked default). SignalR-synthesised rows (handleSessionCreated /
 * handleMessageNotification) previously omitted `category` entirely, so a live
 * row rendered with no category chip and, absent a default def, mis-bucketed
 * until a full reload re-hydrated it from SessionDto.category. Seeding it here
 * from the store's own stateDefs closes that gap without a network round-trip.
 */
function defaultCategoryKey(defs: ConversationStateDef[]): string | null {
  const def = defs.find((d) => d.isDefault && d.isActive);
  return def ? def.key : null;
}

/**
 * The ADR-033 fields of a row SYNTHESISED from a push (message, session-created, draft orphan) rather than read from the
 * list. `ownerAgentId` is deliberately `undefined` - "the server did not say" - which every consumer fails closed on;
 * `partial` tells the shell to re-read the row, and keeps the row out of the folder views until it has.
 */
function partialRowFields(at: string): Pick<
  Conversation,
  'ownerAgentId' | 'ownerReleased' | 'tags' | 'tagsVersion' | 'partial' | 'lastActivityAt'
> {
  return {
    ownerAgentId: undefined,
    ownerReleased: false,
    tags: [],
    tagsVersion: 0,
    partial: true,
    lastActivityAt: at,
  };
}

export const useConversationStore = create<ConversationState>((set, get) => ({
  conversations: [],
  activeSessionId: null,
  messages: [],
  consultationLog: {},
  stateDefs: [],
  botThinking: {},
  spamTombstones: {},
  timeline: {},
  pendingProposedReplies: {},
  sentProposedReplies: {},
  folderPages: {},
  folderCounts: {},
  countsLoaded: false,
  lifecycleTab: DEFAULT_LIFECYCLE_TAB,
  needHelpCount: null,
  tagCatalog: [],
  catalogVersion: 0,
  consoleSettings: null,
  consoleConfigVersion: 0,
  searchQuery: '',
  hiddenSessions: {},
  lastWithdrawnId: null,

  isHidden: (sessionId) => sessionId in get().hiddenSessions,

  hideSession: (sessionId) => {
    if (!sessionId) return;
    // Outside set(): clears the safety timer too, which a plain state write would leave armed.
    get().setBotThinking(sessionId, false);
    set((state) => {
      const wasActive = state.activeSessionId === sessionId;
      const consultationLog = { ...state.consultationLog };
      delete consultationLog[sessionId];
      const timeline = { ...state.timeline };
      delete timeline[sessionId];
      return {
        hiddenSessions: { ...state.hiddenSessions, [sessionId]: Date.now() },
        folderPages: withoutSession(state.folderPages, sessionId),
        conversations: state.conversations.filter((c) => c.sessionId !== sessionId),
        consultationLog,
        timeline,
        activeSessionId: wasActive ? null : state.activeSessionId,
        messages: wasActive ? [] : state.messages.filter((m) => m.sessionId !== sessionId),
        lastWithdrawnId: wasActive ? sessionId : state.lastWithdrawnId,
      };
    });
  },

  unhideSession: (sessionId) => {
    set((state) => {
      if (!(sessionId in state.hiddenSessions)) return state;
      const next = { ...state.hiddenSessions };
      delete next[sessionId];
      return {
        hiddenSessions: next,
        lastWithdrawnId: state.lastWithdrawnId === sessionId ? null : state.lastWithdrawnId,
      };
    });
  },

  applyFolderPage: (folder, rows, nextCursor, hasMore, requestedAt, mode) => {
    set((state) => {
      const unhide: string[] = [];
      const accepted: Conversation[] = [];
      const seen = new Set<string>();
      for (const row of rows) {
        const id = row.sessionId;
        if (seen.has(id)) continue;
        // OP-1005 - a spam tombstone is the authority for this browser session, whatever the server's page says.
        if (state.spamTombstones[id]) continue;
        const hiddenAt = state.hiddenSessions[id];
        if (hiddenAt !== undefined) {
          // Hidden AFTER this page was requested: the answer predates the hide and may not resurrect the row.
          if (hiddenAt > requestedAt) continue;
          // Listed by a request sent at or after the hide: the server has seen the hide's cause and still lists it.
          unhide.push(id);
        }
        seen.add(id);
        accepted.push(row);
      }

      const byId = new Map(state.conversations.map((c) => [c.sessionId, c]));
      for (const row of accepted) byId.set(row.sessionId, mergeListedRow(byId.get(row.sessionId), row));

      const prevIds = mode === 'append' ? (state.folderPages[folder]?.ids ?? []) : [];
      const ids = [...prevIds];
      const inIds = new Set(prevIds);
      for (const row of accepted) {
        if (inIds.has(row.sessionId)) continue;
        inIds.add(row.sessionId);
        ids.push(row.sessionId);
      }

      let hiddenSessions = state.hiddenSessions;
      let lastWithdrawnId = state.lastWithdrawnId;
      if (unhide.length > 0) {
        hiddenSessions = { ...state.hiddenSessions };
        for (const id of unhide) {
          delete hiddenSessions[id];
          if (lastWithdrawnId === id) lastWithdrawnId = null;
        }
      }

      return {
        conversations: sortByActivity([...byId.values()]),
        hiddenSessions,
        lastWithdrawnId,
        folderPages: {
          ...state.folderPages,
          [folder]: { ids, cursor: nextCursor, hasMore, loading: false, error: false, loadedAt: Date.now() },
        },
      };
    });
  },

  applyRefreshedRow: (row, requestedAt, sentAtClock) => {
    let result: 'applied' | 'hidden' | 'stale' = 'applied';
    set((state) => {
      const id = row.sessionId;
      if (state.spamTombstones[id]) {
        result = 'hidden';
        return state;
      }
      const hiddenAt = state.hiddenSessions[id];
      if (hiddenAt !== undefined && hiddenAt > requestedAt) {
        result = 'hidden';
        return state;
      }
      if (sentAtClock !== undefined && (rowPushedAt.get(id) ?? 0) > sentAtClock) {
        result = 'stale';
        return state;
      }
      const prev = state.conversations.find((c) => c.sessionId === id);
      // A read of the WHOLE row is complete by definition: it is no longer a row synthesised from a push.
      const merged: Conversation = { ...mergeListedRow(prev, row), partial: false };
      const conversations = prev
        ? state.conversations.map((c) => (c.sessionId === id ? merged : c))
        : [...state.conversations, merged];
      let hiddenSessions = state.hiddenSessions;
      let lastWithdrawnId = state.lastWithdrawnId;
      if (hiddenAt !== undefined) {
        hiddenSessions = { ...state.hiddenSessions };
        delete hiddenSessions[id];
        if (lastWithdrawnId === id) lastWithdrawnId = null;
      }
      return { conversations: sortByActivity(conversations), hiddenSessions, lastWithdrawnId };
    });
    return result;
  },

  setFolderLoading: (folder, loading) => {
    set((state) => {
      const prev = state.folderPages[folder] ?? emptyFolderPage();
      return {
        folderPages: {
          ...state.folderPages,
          [folder]: { ...prev, loading, error: loading ? false : prev.error },
        },
      };
    });
  },

  setFolderError: (folder) => {
    set((state) => {
      const prev = state.folderPages[folder] ?? emptyFolderPage();
      return { folderPages: { ...state.folderPages, [folder]: { ...prev, loading: false, error: true } } };
    });
  },

  forgetSearchPages: () => {
    set((state) => {
      const keys = Object.keys(state.folderPages).filter((k) => k.startsWith('search:'));
      if (keys.length === 0) return state;
      const next = { ...state.folderPages };
      for (const k of keys) delete next[k];
      return { folderPages: next };
    });
  },

  resetFolderPages: () => {
    set((state) => ({
      folderPages: {},
      conversations: state.conversations.filter((c) => c.sessionId === state.activeSessionId),
    }));
  },

  setFolderCounts: (counts) => {
    set({ folderCounts: { ...counts }, countsLoaded: true });
  },

  setLifecycleTab: (lifecycleTab) => {
    set((state) => (state.lifecycleTab === lifecycleTab ? state : { lifecycleTab }));
  },

  setNeedHelpCount: (needHelpCount) => {
    set({ needHelpCount });
  },

  setTagCatalog: (tagCatalog) => {
    set({ tagCatalog: Array.isArray(tagCatalog) ? tagCatalog : [] });
  },

  setConsoleSettings: (consoleSettings) => {
    set({ consoleSettings });
  },

  setSearchQuery: (searchQuery) => {
    set({ searchQuery });
  },

  resetAll: () => {
    for (const timer of botThinkingTimers.values()) clearTimeout(timer);
    botThinkingTimers.clear();
    pendingClaims.clear();
    rowPushedAt.clear();
    set({
      conversations: [],
      activeSessionId: null,
      messages: [],
      botThinking: {},
      spamTombstones: {},
      consultationLog: {},
      stateDefs: [],
      timeline: {},
      folderPages: {},
      folderCounts: {},
      countsLoaded: false,
      lifecycleTab: DEFAULT_LIFECYCLE_TAB,
      needHelpCount: null,
      tagCatalog: [],
      catalogVersion: 0,
      consoleSettings: null,
      consoleConfigVersion: 0,
      searchQuery: '',
      hiddenSessions: {},
      lastWithdrawnId: null,
      pendingProposedReplies: {},
      sentProposedReplies: {},
    });
  },

  handleSessionOwnershipChanged: (n, ctx) => {
    const id = n?.sessionId;
    if (!id) return;
    // A malformed push (owner neither a string nor an explicit null) is not a statement about ownership; the caller's
    // row re-read is what settles it.
    if (typeof n.ownerAgentId !== 'string' && n.ownerAgentId !== null) return;
    const state = get();
    if (state.spamTombstones[id]) return;
    const row = state.conversations.find((c) => c.sessionId === id);
    const version = Number.isFinite(n.ownerVersion) ? n.ownerVersion : 0;
    // A late push must not undo a newer claim.
    if (row && version < (row.ownerVersion ?? 0)) return;

    touchRow(id);
    const me = ctx?.me || null;
    const viewAll = ctx?.viewAll === true;
    const someoneElse = n.ownerAgentId !== null && n.ownerAgentId !== me;

    // Private ownership is the ROW's own wire statement (`ownershipEnforced`), never a guess: PR-A1 records a claim in
    // EVERY tenant, and a tenant where it is not in effect must keep showing the conversation to everyone. A row that is
    // not loaded says nothing either, so it is not hidden here: the caller's re-read (onRowStale) asks the server, and a
    // 404 hides it then.
    if (someoneElse && !viewAll && row?.ownershipEnforced === true) {
      // Defence in depth: the server stopped delivering this conversation to us, the console stops showing it.
      clearPendingClaim(id);
      get().hideSession(id);
      return;
    }

    set((st) => ({
      conversations: st.conversations.map((c) => {
        if (c.sessionId !== id) return c;
        const ownerChanged = (c.ownerAgentId ?? null) !== n.ownerAgentId;
        const uiState = n.state && c.state !== 'CLOSED' ? toUiState(n.state) : c.state;
        return {
          ...c,
          ownerAgentId: n.ownerAgentId,
          ownerReleased: n.ownerReleased === true,
          ownerVersion: version,
          ownerSince:
            n.ownerAgentId === null ? null : ownerChanged ? (n.timestamp ?? c.ownerSince ?? null) : c.ownerSince,
          ownerSource:
            n.ownerAgentId === null
              ? null
              : ownerChanged && ['guidance', 'takeover', 'draft_approve', 'backfill'].includes(n.reason)
              ? n.reason
              : c.ownerSource,
          state: uiState,
        };
      }),
    }));
    if (n.state && toUiState(n.state) === 'HUMAN_TAKEOVER') get().setBotThinking(id, false);
    // The claim (if any) is settled either way: ownership is now this push's owner.
    clearPendingClaim(id);
    // Mine, or nobody's: this console may show it. (A viewAll viewer of someone else's keeps whatever it had.)
    if (!someoneElse) get().unhideSession(id);
  },

  handleSessionWithdrawn: (n, ctx) => {
    const id = n?.sessionId;
    if (!id) return 'ignore';
    if (ctx?.viewAll === true) return 'ignore';
    const me = ctx?.me || null;
    const row = get().conversations.find((c) => c.sessionId === id);
    if ((me !== null && row?.ownerAgentId === me) || hasPendingClaim(id)) return 'verify';
    // Hide only a row that SAYS private ownership is in effect. Where it is not (or the row is not loaded, so nothing says
    // anything), the push may be one the server sent for a tenant whose claims are only records: the server stays the
    // arbiter - re-read, and a 404 hides it then.
    if (row?.ownershipEnforced !== true) return 'verify';
    get().hideSession(id);
    return 'hide';
  },

  handleSessionShared: (n) => {
    const id = n?.sessionId;
    if (!id) return;
    touchRow(id);
    get().unhideSession(id);
    // The push is content-free and carries NO version, so it cannot be ordered against the ownership pushes: a Shared that
    // arrives late (after a newer claim) must not be taken at its word. Writing `null` here said "the server stated that
    // nobody owns this" - and a row that was just claimed would briefly look team-shared (offered, listed and un-locked
    // for everyone). The owner becomes UNKNOWN instead (`undefined`: the server did not say), which every consumer fails
    // closed on - the folders match nothing, an enforced row is hidden from a viewer who cannot see everyone, the bar and
    // the editors go read-only - until the re-read the caller fires (onRowStale) replaces it with the server's word.
    // The facts that describe the previous owner (release flag, since, source) are cleared; they are not authority.
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.sessionId === id
          ? { ...c, ownerAgentId: undefined, ownerReleased: false, ownerSince: null, ownerSource: null }
          : c,
      ),
    }));
  },

  handleSessionTagsChanged: (n) => {
    if (!n?.sessionId) return;
    get().setConversationTags(n.sessionId, n.tags, n.tagsVersion);
  },

  setConversationTags: (sessionId, tags, tagsVersion) => {
    if (!Array.isArray(tags) || !Number.isFinite(tagsVersion)) return;
    set((state) => {
      const row = state.conversations.find((c) => c.sessionId === sessionId);
      // Only a NEWER version is applied: a late push or a slow answer must not roll the tag set back.
      if (!row || tagsVersion <= (row.tagsVersion ?? 0)) return state;
      return {
        conversations: state.conversations.map((c) =>
          c.sessionId === sessionId ? { ...c, tags: [...tags], tagsVersion } : c,
        ),
      };
    });
  },

  handleTagCatalogChanged: () => {
    set((state) => ({ catalogVersion: state.catalogVersion + 1 }));
  },

  handleConsoleConfigChanged: () => {
    set((state) => ({ consoleConfigVersion: state.consoleConfigVersion + 1 }));
  },

  handleSessionSubjectChanged: (n) => {
    if (!n?.sessionId) return;
    get().setConversationSubject(n.sessionId, n.subject ?? null);
  },

  markSpamHidden: (sessionId) => {
    if (!sessionId) return;
    // Outside set(): clears the safety timer too, which a plain state write would leave armed.
    get().setBotThinking(sessionId, false);
    set((state) => {
      const wasActive = state.activeSessionId === sessionId;
      const consultationLog = { ...state.consultationLog };
      delete consultationLog[sessionId];
      const timeline = { ...state.timeline };
      delete timeline[sessionId];
      return {
        spamTombstones: { ...state.spamTombstones, [sessionId]: true as const },
        folderPages: withoutSession(state.folderPages, sessionId),
        conversations: state.conversations.filter((c) => c.sessionId !== sessionId),
        consultationLog,
        timeline,
        activeSessionId: wasActive ? null : state.activeSessionId,
        messages: wasActive
          ? []
          : state.messages.filter((m) => m.sessionId !== sessionId),
      };
    });
  },

  clearSpamTombstone: (sessionId) => {
    set((state) => {
      if (!(sessionId in state.spamTombstones)) return state;
      const next = { ...state.spamTombstones };
      delete next[sessionId];
      return { spamTombstones: next };
    });
  },

  handleSessionHidden: (notification) => {
    get().markSpamHidden(notification?.sessionId);
  },

  handleSessionRestored: (notification) => {
    if (!notification?.sessionId) return;
    get().clearSpamTombstone(notification.sessionId);
  },

  setBotThinking: (sessionId, thinking) => {
    const existingTimer = botThinkingTimers.get(sessionId);
    if (existingTimer) {
      clearTimeout(existingTimer);
      botThinkingTimers.delete(sessionId);
    }
    if (thinking) {
      botThinkingTimers.set(
        sessionId,
        setTimeout(() => {
          botThinkingTimers.delete(sessionId);
          set((state) => {
            if (!state.botThinking[sessionId]) return state;
            const next = { ...state.botThinking };
            delete next[sessionId];
            return { botThinking: next };
          });
        }, BOT_THINKING_SAFETY_TIMEOUT_MS),
      );
    }
    set((state) => {
      if (thinking) {
        return { botThinking: { ...state.botThinking, [sessionId]: true } };
      }
      if (!(sessionId in state.botThinking)) return state;
      const next = { ...state.botThinking };
      delete next[sessionId];
      return { botThinking: next };
    });
  },

  setConversations: (conversations) => {
    set((state) => {
      // Carry `pendingDraft` across the replacement. It arrives on the SignalR push (or
      // the rehydrate fetch), never on the session list, so a plain replace dropped the
      // review card every time the list reloaded.
      const priorDrafts = new Map(
        state.conversations
          .filter((c) => c.pendingDraft)
          .map((c) => [c.sessionId, c.pendingDraft!]),
      );

      // OP-1005 - a reload whose GET was already in flight when a conversation was marked as spam
      // can still carry it. The tombstone, not the server's list, is the authority for this
      // browser session.
      const merged = conversations
        .filter((c) => !isBlocked(state, c.sessionId))
        .map((c) => {
          const prior = priorDrafts.get(c.sessionId);
          return prior && !c.pendingDraft ? { ...c, pendingDraft: prior } : c;
        });

      return { conversations: sortByActivity(merged) };
    });
  },

  hydratePendingDrafts: (allDrafts, myAgentId) => {
    set((state) => {
      // OP-1005 - the OP-582 orphan block below BUILDS a row out of a draft whose session is not in
      // the list, which is exactly how a conversation marked as spam would have reappeared: the
      // server's pending-drafts feed can still name it for a moment. A tombstoned session has no
      // draft worth showing (they were cancelled and are not restored).
      const drafts = allDrafts.filter((d) => !isBlocked(state, d.sessionId));
      if (drafts.length === 0) return state;

      const bySession = new Map(drafts.map((d) => [d.sessionId, d]));
      let changed = false;

      const toPending = (dto: PendingEmailDraftDto): PendingEmailDraft => ({
        draftId: dto.draftId,
        draftText: dto.draftText,
        attachments: dto.attachments,
        revision: dto.revision,
        customerLastMessage: dto.customerLastMessage,
        createdAtUtc: dto.createdAtUtc,
        maxWaitAtUtc: dto.maxWaitAtUtc,
        idleMinutes: dto.idleMinutes,
        // OP-582 — verbatim, including undefined (see PendingEmailDraft.autoSendEnabled).
        autoSendEnabled: dto.autoSendEnabled,
        assignedAgentId: dto.assignedAgentId ?? null,
        // ADR-019 D6 — derived here, not on the wire: the rehydrate endpoint serves one
        // response to any staff caller, so ownership is relative to THIS console. An
        // unclaimed draft (no assignee) is actionable by whoever opens it first.
        assignedToOther: Boolean(
          dto.assignedAgentId && myAgentId && dto.assignedAgentId !== myAgentId,
        ),
      });

      const seen = new Set<string>();
      const conversations = state.conversations.map((c) => {
        const dto = bySession.get(c.sessionId);
        if (!dto) return c;
        seen.add(c.sessionId);

        // Never overwrite a card the live push already put here for the SAME draft at the
        // same-or-newer revision: the push carries `assignedToOther` and any local
        // `actionRejected` notice, and a slower REST response must not undo either.
        const existing = c.pendingDraft;
        if (existing && existing.draftId === dto.draftId && existing.revision >= dto.revision) {
          return c;
        }

        changed = true;
        return { ...c, pendingDraft: toPending(dto) };
      });

      // OP-582 — a draft whose session is NOT in the loaded list used to be dropped on the
      // floor. The session list is one page (pageSize 100, excludeClosed) and a held draft can
      // wait indefinitely, so on a busy tenant the draft that has been neglected LONGEST is
      // exactly the one that falls off the page — and it then had no card, no row and no count
      // anywhere. Materialise a minimal placeholder row from the draft itself so it is at least
      // visible, countable and openable. Everything here comes from the rehydrate payload; no
      // extra call, no invented endpoint. A later session-list load replaces the placeholder
      // with the real row and `setConversations` carries `pendingDraft` across.
      const orphans: Conversation[] = [];
      for (const dto of drafts) {
        if (seen.has(dto.sessionId)) continue;
        if (state.conversations.some((c) => c.sessionId === dto.sessionId)) continue;
        changed = true;
        orphans.push({
          sessionId: dto.sessionId,
          tenantId: dto.tenantId,
          customerId: '',
          channel: dto.channel ?? 'email',
          state: 'AI_HANDLING',
          slaUrgency: 'NORMAL',
          slaDeadlineUtc: dto.maxWaitAtUtc,
          lastMessageContent: dto.customerLastMessage,
          lastMessageTimestamp: dto.createdAtUtc,
          unreadCount: 0,
          createdAt: dto.createdAtUtc,
          metadata: {},
          category: null,
          // Must NOT be 0: ConversationList hides message-less rows from every folder and
          // every folder count, which would re-hide the very draft this block exists to surface.
          messageCount: 1,
          pendingDraft: toPending(dto),
          ...partialRowFields(dto.createdAtUtc),
        });
      }

      if (!changed) return state;
      return { conversations: sortByActivity([...conversations, ...orphans]) };
    });
  },

  setStateDefs: (stateDefs) => {
    set({ stateDefs });
  },

  upsertConversation: (conversation) => {
    // OP-1005 - the one door every row-creating push walks through (session-created, a message that
    // synthesises a row, the deep-link hydrate). A tombstoned conversation never comes back through it.
    // ADR-033 - and likewise a conversation in the reversible hidden set (withdrawn / not mine / 404).
    if (isBlocked(get(), conversation.sessionId)) return;
    const current = get().conversations;
    const index = current.findIndex((c) => c.sessionId === conversation.sessionId);
    let updated: Conversation[];
    if (index >= 0) {
      updated = [...current];
      updated[index] = conversation;
    } else {
      updated = [...current, conversation];
    }
    set({ conversations: sortByActivity(updated) });
  },

  removeConversation: (sessionId) => {
    set((state) => ({
      conversations: state.conversations.filter((c) => c.sessionId !== sessionId),
      activeSessionId: state.activeSessionId === sessionId ? null : state.activeSessionId,
    }));
  },

  setActiveSession: (sessionId) => {
    // 2026-07-23 — re-selecting the already-open conversation (a second
    // click, or a genuine double-click) must be a no-op: this used to
    // unconditionally wipe `messages` even when the id didn't change, and
    // since ChatView's fetch effect is keyed only on sessionId, nothing
    // ever re-populated it — the transcript stayed empty until the agent
    // switched away and back. A real switch still clears + refetches.
    // `timeline: {}` on a real switch: only the open conversation's timeline is kept (see `timeline`).
    set((state) =>
      state.activeSessionId === sessionId
        ? state
        : {
            activeSessionId: sessionId,
            messages: [],
            timeline: {},
            // 2026-11-04 - only the OPEN conversation's content is held: the consultation log of the one just left is dropped
            // (the transcript read repopulates it when the agent returns). A push for a conversation that is not open
            // simply starts a fresh, small entry until the next switch.
            consultationLog: {},
            // Opening something else ends the "that conversation is no longer available" notice for the previous one.
            lastWithdrawnId: sessionId ? null : state.lastWithdrawnId,
          },
    );
  },

  setMessages: (messages) => {
    set({ messages });
  },

  addMessage: (message) => {
    const { activeSessionId } = get();
    if (message.sessionId === activeSessionId) {
      set((state) => ({ messages: [...state.messages, message] }));
    }
    // Also update the lastMessage preview in the conversation list
    set((state) => ({
      conversations: sortByActivity(
        state.conversations.map((c) =>
          c.sessionId === message.sessionId
            ? {
                ...c,
                lastMessageContent: message.content,
                lastMessageTimestamp: message.timestamp,
                // Server parity: a message of either direction is activity; the direction picks which stamp moves.
                lastActivityAt: laterIso(c.lastActivityAt, message.timestamp),
                lastCustomerMessageAt:
                  message.sender === 'CUSTOMER'
                    ? laterIso(c.lastCustomerMessageAt, message.timestamp)
                    : c.lastCustomerMessageAt,
                lastOutboundAt:
                  message.sender === 'CUSTOMER' ? c.lastOutboundAt : laterIso(c.lastOutboundAt, message.timestamp),
                unreadCount:
                  message.sessionId === state.activeSessionId
                    ? c.unreadCount
                    : c.unreadCount + 1,
              }
            : c
        )
      ),
    }));
  },

  appendConsultationMessage: (sessionId, incoming, opts) => {
    set((state) => {
      // OP-1005 - the one door into the Bot Consultation log (consultation push, follow-up, the
      // guidance-failed alert, the consultation rehydrate, the transcript loader). Unlike the row
      // actions it is NOT row-gated, so without this a late push would re-create a log entry for a
      // conversation the agent has already marked as spam.
      if (isBlocked(state, sessionId)) return state;
      const existing = state.consultationLog[sessionId] ?? [];

      // 2026-10-28 - proposed replies pushed BEFORE this note was in the log are attached to it now, and released.
      const earlyKey = proposedReplyKey(sessionId, incoming.messageId);
      const early = incoming.sender === 'AI' ? state.pendingProposedReplies[earlyKey] : undefined;
      const message: Message =
        early && !(incoming.proposedReplies && incoming.proposedReplies.length > 0)
          ? { ...incoming, proposedReplies: early }
          : incoming;
      const pendingAfter = (() => {
        if (!early) return state.pendingProposedReplies;
        const next = { ...state.pendingProposedReplies };
        delete next[earlyKey];
        return next;
      })();
      // A duplicate copy of a note that is already held is dropped - but replies it carries (and the held note lacks)
      // are copied onto the held note, so a push-built card gains the replies the stored row brings.
      const adoptReplies = (at: number) => {
        const held = existing[at];
        if (!(message.proposedReplies && message.proposedReplies.length > 0)) return null;
        if (held.proposedReplies && held.proposedReplies.length > 0) return null;
        const next = [...existing];
        next[at] = { ...held, proposedReplies: message.proposedReplies };
        return { consultationLog: { ...state.consultationLog, [sessionId]: next }, pendingProposedReplies: pendingAfter };
      };
      const unchanged = early ? { pendingProposedReplies: pendingAfter } : state;
      // OP-1516 (live 2026-10-27) — the persisted row wins. The start-up rehydrate from the queue used to hold the re-ask's
      // messageId with the item's MERGED context stamped at the item's creation (9:25), and the real 9:31 row was then
      // dropped below as its duplicate: the re-ask rendered as a second 9:25 bubble repeating the first ask.
      if (opts?.authoritative) {
        const sameNote = (m: Message) =>
          m.messageId === message.messageId ||
          (message.sender === 'AI' &&
            message.messageId.startsWith('bot-consult-') &&
            m.sender === 'AI' &&
            m.messageId.startsWith('bot-consult-') &&
            (m.content ?? '').trim() === (message.content ?? '').trim());
        const at = existing.findIndex(sameNote);
        if (at >= 0) {
          const next = existing
            .filter((m, i) => i === at || !sameNote(m))
            // 2026-10-28 - a stored row read before its proposed replies were saved keeps the ones a push brought.
            .map((m) => (sameNote(m) ? keepProposedReplies(message, m) : m));
          return { consultationLog: { ...state.consultationLog, [sessionId]: next }, pendingProposedReplies: pendingAfter };
        }
      }
      // De-dup by messageId — a SignalR re-broadcast or a double-render
      // shouldn't post the same bot status twice.
      const sameIdAt = existing.findIndex((m) => m.messageId === message.messageId);
      if (sameIdAt >= 0) return adoptReplies(sameIdAt) ?? unchanged;
      // OP-1075 (tester run 2026-10-05) - the same bot note arrived twice for a console opened after the consultation:
      // the rehydrate keys it on the escalation row's transactionId, which assignment and status changes overwrite,
      // while the transcript and the live push key it on the consultation's own. Same bot note, same text: one entry.
      if (message.sender === 'AI' && message.messageId.startsWith('bot-consult-')) {
        const sameTextAt = existing.findIndex(
          (m) =>
            m.sender === 'AI' &&
            m.messageId.startsWith('bot-consult-') &&
            (m.content ?? '').trim() === (message.content ?? '').trim(),
        );
        if (sameTextAt >= 0) return adoptReplies(sameTextAt) ?? unchanged;
      }

      // 2026-07-23 — reconcile the agent's own optimistic guidance echo
      // (messageId `local-guidance-*`, pushed straight into local state on
      // send — ChatView.sendGuidance) against the later-arriving REAL row
      // (persisted as `agent-guidance-{sessionId}-{ticks}`, or delivered the
      // same way over the hub), which never shares the same id as the
      // optimistic one. Without this, every subsequent transcript reload
      // appended the real row alongside the still-resident optimistic one —
      // two pixel-identical "You" bubbles. Match by sender + content + a
      // small timestamp window instead of exact id, in whichever order the
      // two arrive.
      const isOptimisticGuidance = (m: Message) => m.messageId.startsWith('local-guidance-');
      const withinWindow = (a: string, b: string) =>
        Math.abs(new Date(a).getTime() - new Date(b).getTime()) <= 15_000;

      if (message.sender === 'AGENT') {
        if (!isOptimisticGuidance(message)) {
          // The real row arrived — replace a matching still-resident
          // optimistic entry in place rather than appending a duplicate.
          const optimisticIdx = existing.findIndex(
            (m) =>
              isOptimisticGuidance(m) &&
              m.content === message.content &&
              withinWindow(m.timestamp, message.timestamp),
          );
          if (optimisticIdx >= 0) {
            const next = [...existing];
            next[optimisticIdx] = message;
            return { consultationLog: { ...state.consultationLog, [sessionId]: next } };
          }
        } else {
          // The optimistic echo itself — if the real row already arrived
          // first (reverse race), skip adding a second bubble.
          const alreadyReal = existing.some(
            (m) =>
              !isOptimisticGuidance(m) &&
              m.sender === 'AGENT' &&
              m.content === message.content &&
              withinWindow(m.timestamp, message.timestamp),
          );
          if (alreadyReal) return state;
        }
      }

      return {
        consultationLog: {
          ...state.consultationLog,
          [sessionId]: [...existing, message],
        },
        pendingProposedReplies: pendingAfter,
      };
    });
  },

  handleConsultationProposedReplies: (notification) => {
    const sessionId = (notification?.sessionId ?? '').trim();
    const noteMessageId = (notification?.noteMessageId ?? '').trim();
    const replies = normaliseProposedReplies(notification?.replies);
    if (!sessionId || !noteMessageId || !replies) return;
    set((state) => {
      // OP-1005 - nothing re-enters a conversation marked as spam (or hidden from this viewer).
      if (isBlocked(state, sessionId)) return state;
      const log = state.consultationLog[sessionId] ?? [];
      // Attached ONLY to the bot note with exactly this id in exactly this session: a reply is never moved onto another
      // conversation's note, nor onto a staff row.
      const at = log.findIndex((m) => m.messageId === noteMessageId && m.sender === 'AI' && m.stream === 'consult');
      if (at >= 0) {
        const next = [...log];
        next[at] = { ...log[at], proposedReplies: replies };
        return { consultationLog: { ...state.consultationLog, [sessionId]: next } };
      }
      // The note is not on screen yet: hold the replies for it (bounded, oldest dropped first).
      const key = proposedReplyKey(sessionId, noteMessageId);
      const held = { ...state.pendingProposedReplies, [key]: replies };
      const keys = Object.keys(held);
      if (keys.length > MAX_PENDING_PROPOSED) {
        for (const k of keys.slice(0, keys.length - MAX_PENDING_PROPOSED)) delete held[k];
      }
      return { pendingProposedReplies: held };
    });
  },

  markProposedReplySent: (sessionId, noteMessageId, index) => {
    if (!sessionId || !noteMessageId || !Number.isInteger(index)) return;
    set((state) => ({
      sentProposedReplies: { ...state.sentProposedReplies, [proposedReplyKey(sessionId, noteMessageId)]: index },
    }));
  },

  clearConsultationMessages: (sessionId) => {
    set((state) => {
      if (!(sessionId in state.consultationLog)) return state;
      const next = { ...state.consultationLog };
      delete next[sessionId];
      return { consultationLog: next };
    });
  },

  hydrateOpenConsultations: (items, myAgentId) => {
    // OP-1005 - a conversation marked as spam has its open consultation dropped server-side, but the
    // queue feed can still name it for a moment; never decorate (or log for) a tombstoned session.
    const gate = get();
    const open = items.filter(
      (i) => (i.status === 'PENDING' || i.status === 'ASSIGNED') && !isBlocked(gate, i.sessionId),
    );
    if (open.length === 0) return;

    // Newest open item per session wins (a session can be re-asked; OP-955 keeps one clock, but the queue
    // may still hold an older row for a moment).
    const bySession = new Map<string, EscalationItemDto>();
    for (const item of open) {
      const prev = bySession.get(item.sessionId);
      if (!prev || new Date(item.createdAt).getTime() > new Date(prev.createdAt).getTime()) {
        bySession.set(item.sessionId, item);
      }
    }

    const marked: EscalationItemDto[] = [];
    set((state) => {
      let changed = false;
      const next = state.conversations.map((c) => {
        const item = bySession.get(c.sessionId);
        if (!item || c.state === 'CLOSED' || c.state === 'HUMAN_TAKEOVER') return c;

        const assignedToOther = !!item.assignedAgentId && item.assignedAgentId !== myAgentId;
        const sameTx = !!item.transactionId && c.consultationTransactionId === item.transactionId;
        const keepActionable = sameTx && c.assignedToOther === false;

        changed = true;
        marked.push(item);
        return {
          ...c,
          state: 'CONSULTATION_REQUESTED' as SessionState,
          slaUrgency: 'WARNING' as const,
          consultationBotId: item.botId || c.consultationBotId,
          assignedToOther: keepActionable ? false : assignedToOther,
          consultationTransactionId: item.transactionId || c.consultationTransactionId,
        };
      });
      return changed ? { conversations: sortByActivity(next) } : state;
    });

    for (const item of marked) {
      const ask = consultationAskOf(item);
      if (!ask || !item.transactionId) continue;
      get().appendConsultationMessage(item.sessionId, {
        messageId: `bot-consult-${item.sessionId}-${item.transactionId}`,
        sessionId: item.sessionId,
        sender: 'AI',
        senderId: item.botId ?? '',
        content: ask.content,
        contentType: 'text',
        timestamp: ask.at,
        stream: 'consult',
      });
    }
  },

  handleConsultation: (notification) => {
    // OP-1005 - a late consultation push for a conversation marked as spam: ignore it whole (row
    // update AND the Bot Consultation log entry).
    if (isBlocked(get(), notification.sessionId)) return;
    touchRow(notification.sessionId);

    // 1) Mark the conversation as CONSULTATION_REQUESTED for the row.
    const current = get().conversations;
    const index = current.findIndex((c) => c.sessionId === notification.sessionId);
    if (index >= 0) {
      const prev = current[index];
      // ADR-016 §D8 — targeted routing delivers TWO copies for one
      // consultation: an ACTIONABLE copy (assignedToOther=false) to the
      // assignee and an AWARENESS copy (assignedToOther=true) to the tenant
      // group. Dedupe on (sessionId, transactionId) PREFERRING the actionable
      // copy: once we've recorded the actionable copy for a TX, a later
      // awareness duplicate must not downgrade the row to read-only (order of
      // arrival on the bus is not guaranteed). When routing is OFF every copy
      // arrives assignedToOther=false ⇒ unchanged, fully-claimable behavior.
      const incomingAssignedToOther = notification.assignedToOther === true;
      const sameTx =
        !!notification.transactionId &&
        prev.consultationTransactionId === notification.transactionId;
      const alreadyActionable = sameTx && prev.assignedToOther === false;
      const assignedToOther = alreadyActionable ? false : incomingAssignedToOther;

      // 2026-10-28 - the push carries no reason. A reason this row holds from an EARLIER consultation (older than this
      // push by more than clock skew) is dropped; this consultation's own timeline entry sets it again.
      const reasonMs = Date.parse(prev.consultationReason?.at ?? '');
      const pushMs = Date.parse(notification.timestamp ?? '');
      const staleReason =
        !!prev.consultationReason &&
        (!Number.isFinite(reasonMs) || (Number.isFinite(pushMs) && reasonMs < pushMs - REASON_SKEW_MS));

      // 2026-10-28 (later the same day): the hub now sends the reason on the push (null on the ADR-016 routed card). When it
      // is there it IS this consultation's reason, so the "asks for a person" badge works without opening the row.
      const pushReason =
        typeof notification.reason === 'string' && notification.reason.trim()
          ? { code: notification.reason.trim(), at: notification.timestamp || new Date().toISOString() }
          : undefined;

      const updated = [...current];
      updated[index] = {
        ...prev,
        ...(pushReason ? { consultationReason: pushReason } : staleReason ? { consultationReason: undefined } : {}),
        state: 'CONSULTATION_REQUESTED',
        slaUrgency: 'WARNING',
        // Capture the consultation's botId so the "Let bot continue" button
        // can call DismissConsultation without a separate lookup.
        consultationBotId: notification.botId,
        assignedToOther,
        consultationTransactionId:
          notification.transactionId ?? prev.consultationTransactionId,
        lastMessageContent: notification.lastCustomerMessage,
        lastMessageTimestamp: notification.timestamp,
        // Server parity: a consultation request bumps lastActivityAt (it does not move the customer/outbound stamps).
        lastActivityAt: laterIso(prev.lastActivityAt, notification.timestamp),
      };
      set({ conversations: sortByActivity(updated) });
    }

    // 2) Push a synthetic Bot message into the Bot Consultation pane so
    //    the agent sees what the bot is asking. The bot's PipelineService
    //    builds a rich multi-line "status report" string in
    //    notification.context — customer message, what was retrieved,
    //    confidence score, suggested guidance shape.
    //
    //    OP-021 (2026-05-06) — messageId is keyed by transactionId, NOT
    //    by timestamp. The DB-hydrated transcript row from
    //    ConversationStoreService uses the same `bot-consult-{sessionId}-{TX}`
    //    format, so reopening a session does NOT produce a duplicate row:
    //    appendConsultationMessage's `existing.some(m.messageId===new.messageId)`
    //    dedupe collapses both sources into one. Pre-fix the SignalR side
    //    used the ISO timestamp string while the DB side used Ticks — the
    //    keys never matched and both rendered. Falls back to timestamp
    //    only if a stale notification arrives without transactionId.
    if (notification.context && notification.context.trim().length > 0) {
      const dedupeKey = notification.transactionId || notification.timestamp;
      const synthetic: Message = {
        messageId: `bot-consult-${notification.sessionId}-${dedupeKey}`,
        sessionId: notification.sessionId,
        sender: 'AI',
        senderId: notification.botId,
        content: notification.context,
        contentType: 'text',
        timestamp: notification.timestamp,
        stream: 'consult',
      };
      get().appendConsultationMessage(notification.sessionId, synthetic);
    }
  },

  handleEmailDraft: (notification) => {
    set((state) => {
      // OP-1005 - the drafts of a conversation marked as spam were cancelled server-side and are NOT
      // restored by an Undo, so a draft push for a tombstoned session is never rendered.
      if (isBlocked(state, notification.sessionId)) return state;
      const index = state.conversations.findIndex((c) => c.sessionId === notification.sessionId);
      // Unknown row — a draft has nowhere to render; the next session-list
      // load will not resurrect it. Accepted v1 gap (ADR-019 "Revisit:
      // console rehydrate of in-flight drafts").
      if (index < 0) return state;

      const prev = state.conversations[index];
      const existing = prev.pendingDraft;

      // A fresh (non-merge) draft always clears actionRejected — a new
      // revision means any stale "not owner" notice from a prior revision
      // no longer applies.
      const buildFresh = (): PendingEmailDraft => ({
        draftId: notification.draftId,
        draftText: notification.draftText,
        attachments: notification.attachments,
        revision: notification.revision,
        customerLastMessage: notification.customerLastMessage,
        createdAtUtc: notification.createdAtUtc,
        maxWaitAtUtc: notification.maxWaitAtUtc,
        idleMinutes: notification.idleMinutes,
        // OP-582 — carried verbatim, INCLUDING undefined. `undefined` means "the server did
        // not say", which the card reads as auto-send on; coercing it to a boolean here would
        // erase the difference between "not said" and "held".
        autoSendEnabled: notification.autoSendEnabled,
        assignedAgentId: notification.assignedAgentId,
        assignedToOther: notification.assignedToOther,
        revisionFailureReason: notification.revisionFailureReason,
        actionRejected: undefined,
      });

      let nextPendingDraft: PendingEmailDraft;

      if (existing && existing.draftId === notification.draftId) {
        if (notification.revision < existing.revision) {
          // Late lower revision — bus ordering is not guaranteed. A late
          // lower revision must never clobber a newer one: ignore the WHOLE
          // notification.
          return state;
        }
        if (notification.revision === existing.revision) {
          // Same-revision dual copy (targeted actionable + tenant awareness) —
          // mirrors handleConsultation's ADR-016 §D8 dedupe. Merge PREFERRING
          // THE ACTIONABLE COPY: once the owner's actionable copy
          // (assignedToOther=false) has been recorded, a same-revision
          // awareness duplicate (true) must never downgrade it to read-only,
          // regardless of arrival order. Preserve `actionRejected` — this is
          // the same draft, not a new one.
          const incomingAssignedToOther = notification.assignedToOther === true;
          const assignedToOther =
            existing.assignedToOther === false ? false : incomingAssignedToOther;
          nextPendingDraft = {
            ...buildFresh(),
            assignedToOther,
            actionRejected: existing.actionRejected,
          };
        } else {
          // Higher revision — replace unconditionally.
          nextPendingDraft = buildFresh();
        }
      } else {
        // No existing draft, or a DIFFERENT draftId — a new draft supersedes
        // an older one unconditionally, even at revision 0.
        nextPendingDraft = buildFresh();
      }

      const updated = [...state.conversations];
      // A draft is not a message: never touch state / slaUrgency /
      // lastMessageContent (must not reorder the inbox or flip a badge), and
      // never append to messages / consultationLog (an unapproved draft has
      // not reached the customer — rendering it as a transcript row would
      // be a lie).
      updated[index] = { ...prev, pendingDraft: nextPendingDraft };
      return { conversations: updated };
    });
  },

  handleEmailDraftResolved: (notification) => {
    set((state) => {
      const index = state.conversations.findIndex((c) => c.sessionId === notification.sessionId);
      if (index < 0) return state;
      const prev = state.conversations[index];
      const pending = prev.pendingDraft;
      // No pending draft, or a resolution for a stale/other draft — must
      // never clear the current card.
      if (!pending || pending.draftId !== notification.draftId) return state;

      const updated = [...state.conversations];
      if (notification.outcome === 'rejectedNotOwner') {
        // Non-terminal — the draft is still pending, just not ours to act
        // on; the card shows a notice instead of vanishing (ADR-019 D6/D9).
        updated[index] = { ...prev, pendingDraft: { ...pending, actionRejected: true } };
      } else {
        // Every other outcome is terminal (approved | edited | autoSentMaxWait
        // | autoSentIdle | cancelled | superseded | failed) — clear so no
        // console keeps offering buttons for an email that already left.
        updated[index] = { ...prev, pendingDraft: undefined };
      }
      return { conversations: updated };
    });
  },

  /**
   * HC1.D (2026-05-13) — render the bot's clarifying question as a new
   * AI message in the existing Bot Consultation pane. Same session card,
   * same dedupe rules as the original ConsultationRequested (keyed on
   * sessionId + transactionId + the special suffix "-followup" so a
   * follow-up under the same TX doesn't collide with the originating
   * consultation row).
   *
   * Composition shape: the question alone. Operator notes 19/10/26: the
   * examiner's rationale (an internal English note, or a code such as
   * "rewrite_failed") used to be appended as "(_examiner: …_)", which put
   * English text under a question written in the team's own language. The
   * question already says what the bot needs; the rationale stays in the
   * audit trail.
   */
  handleBotConsultationFollowup: (notification) => {
    const question = (notification.question ?? '').trim();
    if (question.length === 0) return;

    const content = question;

    // Dedupe key: same TX as the originating consultation + "-followup"
    // marker. If multiple followups land on the same TX (rare — a bot
    // would only re-ask once per turn typically), the timestamp at the
    // end ensures each one is unique.
    const txKey = notification.transactionId || notification.timestamp;
    const synthetic: Message = {
      messageId: `bot-consult-followup-${notification.sessionId}-${txKey}-${notification.timestamp}`,
      sessionId: notification.sessionId,
      sender: 'AI',
      senderId: notification.botId,
      content,
      contentType: 'text',
      timestamp: notification.timestamp,
      stream: 'consult',
    };
    get().appendConsultationMessage(notification.sessionId, synthetic);
  },

  /**
   * HC2.2 / OP-061 (2026-05-27) — surface a live, operator-visible alert
   * when the bot could not deliver actionable proactive guidance to the
   * customer. Two effects, both intentional:
   *   1. Append a synthetic role="alert" entry to the Bot Consultation pane
   *      (sender AGENT-side framing is wrong here — this is a SYSTEM alert,
   *      so we render it via the `kind: 'alert'` discriminator, not a normal
   *      message bubble). The operator sees it in the pane they're already in.
   *   2. Bump the conversation row's slaUrgency to CRITICAL so the row stands
   *      out in the list even if the operator isn't looking at this session.
   * Deduped by transactionId — a SignalR re-broadcast of the same failure
   * must not double-add the alert (mirrors the consultation/followup dedupe).
   */
  handleProactiveGuidanceDeliveryFailed: (notification) => {
    const txKey = notification.transactionId || notification.timestamp;
    const line = proactiveGuidanceFailureMessage(notification.reason);

    const synthetic: Message = {
      // Dedupe key keyed on transactionId — appendConsultationMessage's
      // `existing.some(m.messageId === new.messageId)` collapses repeats.
      messageId: `proactive-guidance-failed-${notification.sessionId}-${txKey}`,
      sessionId: notification.sessionId,
      sender: 'AI',
      senderId: notification.botId,
      content: line,
      contentType: 'text',
      timestamp: notification.timestamp,
      kind: 'alert',
      stream: 'consult',
    };
    get().appendConsultationMessage(notification.sessionId, synthetic);

    // Bump the row's urgency badge so the failure is visible in the list.
    set((state) => ({
      conversations: sortByActivity(
        state.conversations.map((c) =>
          c.sessionId === notification.sessionId
            ? { ...c, slaUrgency: 'CRITICAL' as SlaUrgency }
            : c,
        ),
      ),
    }));
  },

  handleMessageNotification: (notification) => {
    // OP-1005 - a message for a conversation marked as spam (the mark and the customer's next mail
    // can cross on the bus): no synthesised row, no transcript line, no "bot is composing" dots.
    if (isBlocked(get(), notification.sessionId)) return;

    // Event ordering on the bus isn't guaranteed — a MessageReceived can
    // arrive before our SessionCreated handler has run (or if the agent just
    // opened the console mid-conversation). Synthesize a row so the message
    // has somewhere to land; SessionCreated will be a no-op later (dup-check).
    const existing = get().conversations.find((c) => c.sessionId === notification.sessionId);
    if (!existing) {
      get().upsertConversation({
        sessionId: notification.sessionId,
        tenantId: notification.tenantId,
        customerId: notification.customerId,
        channel: notification.channel,
        state: 'AI_HANDLING',
        slaUrgency: 'NORMAL',
        slaDeadlineUtc: notification.timestamp,
        lastMessageContent: notification.content,
        lastMessageTimestamp: notification.timestamp,
        lastPreview: previewText(notification.content),
        lastPreviewAt: notification.timestamp,
        lastPreviewSender: 'customer',
        lastPreviewAgentName: null,
        unreadCount: 0,
        createdAt: notification.timestamp,
        // ADR-017 D3 — seed the tenant's default category so the synthesised row
        // groups + tags correctly live, not only after a reload.
        category: defaultCategoryKey(get().stateDefs),
        metadata: {},
        // A real message just arrived, so this row is never message-less.
        messageCount: 1,
        lastCustomerMessageAt: notification.timestamp,
        ...partialRowFields(notification.timestamp),
      });
    } else if (!existing.messageCount) {
      // The row already existed (e.g. seeded by handleSessionCreated at 0) — bump
      // it now that an actual message landed, so ConversationList stops hiding it.
      set((state) => ({
        conversations: state.conversations.map((c) =>
          c.sessionId === notification.sessionId
            ? { ...c, messageCount: (c.messageCount ?? 0) + 1 }
            : c
        ),
      }));
    }

    const msg: Message = {
      messageId: notification.messageId,
      sessionId: notification.sessionId,
      sender: 'CUSTOMER',
      senderId: notification.customerId,
      content: notification.content,
      contentType: notification.contentType,
      timestamp: notification.timestamp,
      attachments: notification.attachments,
      stream: 'chat',
    };
    get().addMessage(msg);
    // The row's last-message preview is the customer's, whatever the row said before (the server's next row read confirms it).
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.sessionId === notification.sessionId
          ? {
              ...c,
              lastPreview: previewText(notification.content),
              lastPreviewAt: notification.timestamp,
              lastPreviewSender: 'customer' as const,
              lastPreviewAgentName: null,
            }
          : c,
      ),
    }));
    // A customer turn arrived — the bot is about to compose a reply (or, in
    // an open consultation, at least evaluate one). CustomerChatSurface ANDs
    // this with `!takenOver`, so it's a no-op harmless flag during takeover.
    get().setBotThinking(notification.sessionId, true);
  },

  handleSlaWarning: (notification) => {
    set((state) => ({
      conversations: sortByActivity(
        state.conversations.map((c) =>
          c.sessionId === notification.sessionId
            ? {
                ...c,
                slaUrgency:
                  notification.elapsedMinutes >= notification.thresholdMinutes
                    ? 'CRITICAL'
                    : 'WARNING',
              }
            : c
        )
      ),
    }));
  },

  handleSessionCreated: (notification) => {
    // Build a minimal Conversation from the notification fields. The full
    // SessionDto comes through the REST list on reload, but for live rendering
    // we only need enough to show the row (icon + label + empty preview).
    // OP-1005 / ADR-033 - a conversation that is spam-tombstoned or in the hidden set does not come back through here.
    if (isBlocked(get(), notification.sessionId)) return;
    const existing = get().conversations.find((c) => c.sessionId === notification.sessionId);
    if (existing) return; // duplicate event, e.g., after reconnect

    const fresh: Conversation = {
      sessionId: notification.sessionId,
      tenantId: notification.tenantId,
      customerId: notification.customerId,
      channel: notification.channel,
      customerDisplayName: notification.customerDisplayName,
      state: 'AI_HANDLING',
      slaUrgency: 'NORMAL',
      slaDeadlineUtc: notification.timestamp,
      lastMessageContent: '',
      lastMessageTimestamp: notification.timestamp,
      unreadCount: 0,
      createdAt: notification.timestamp,
      // ADR-017 D3 — seed the tenant's default category so the freshly-created
      // row groups + tags correctly live, not only after a reload.
      category: defaultCategoryKey(get().stateDefs),
      metadata: {},
      // A session is created the instant the widget opens, before the customer
      // has typed anything — messageCount starts at 0 so ConversationList
      // keeps it out of every folder until an actual message bumps it.
      messageCount: 0,
      ...partialRowFields(notification.timestamp),
    };
    get().upsertConversation(fresh);
  },

  handleCustomerDisplayNameUpdated: (notification) => {
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.customerId === notification.customerId
          ? { ...c, customerDisplayName: notification.displayName }
          : c
      ),
    }));
  },

  handleSessionStateChanged: (notification) => {
    touchRow(notification.sessionId);
    const uiState = toUiState(notification.newState);
    if (uiState === 'HUMAN_TAKEOVER') {
      // The dots indicator is gated on `!takenOver` anyway, but clear the
      // flag too so it doesn't linger true if the session later leaves
      // takeover without a fresh customer message to reset it.
      get().setBotThinking(notification.sessionId, false);
    }
    set((state) => ({
      conversations: sortByActivity(
        state.conversations.map((c) => {
          if (c.sessionId !== notification.sessionId) return c;
          // When the session transitions OUT of CONSULTATION_REQUESTED (back
          // to AI_HANDLING / HUMAN_TAKEOVER / closed), clear the WARNING
          // badge and the captured botId so the row goes back to "normal".
          // Without this the orange WARNING pill sticks around even though
          // the bot has already resumed handling.
          const leavingConsultation =
            c.state === 'CONSULTATION_REQUESTED' && uiState !== 'CONSULTATION_REQUESTED';
          const reopened = c.state === 'CLOSED' && uiState !== 'CLOSED';
          return {
            ...c,
            state: uiState,
            slaUrgency: leavingConsultation ? 'NORMAL' : c.slaUrgency,
            consultationBotId: leavingConsultation ? undefined : c.consultationBotId,
            // ADR-016 §D8 — the routed-consultation read-only flag + its dedupe
            // TX are scoped to the OPEN consultation; clear both when the
            // session leaves the consultation state so a future (broadcast or
            // re-routed) consultation starts from a clean, claimable slate.
            assignedToOther: leavingConsultation ? undefined : c.assignedToOther,
            consultationTransactionId: leavingConsultation
              ? undefined
              : c.consultationTransactionId,
            // 2026-10-28 - why the bot consulted belongs to THAT consultation: a later one starts with no reason until its
            // own timeline entry says (so an old "asked for a person" can never decorate a new, different consultation).
            consultationReason: leavingConsultation ? undefined : c.consultationReason,
            // ADR-019 defensive clear — a closed conversation cannot have a
            // live draft awaiting review; without this a stale card lingers
            // forever if the EmailReplyDraftResolved event is ever lost.
            pendingDraft: uiState === 'CLOSED' ? undefined : c.pendingDraft,
            // A REOPEN (CLOSED -> any open state): the row stops being closed - `state` above moves it out of the closed
            // status folder and into the open ones (the folder rules read the state) - and says so.
            ...(reopened
              ? {
                  closedAt: null,
                  reopenedAt: notification.timestamp ?? c.reopenedAt ?? null,
                  reopenCount: (c.reopenCount ?? 0) + 1,
                }
              : {}),
          };
        })
      ),
    }));
  },

  handleSessionClosed: (notification) => {
    // OP-373 — keep the row and mark it CLOSED so it moves into the "Closed"
    // directory section (grouping routes any CLOSED row there) instead of
    // vanishing. The agent can still open it to read the transcript, and the
    // Closed folder shows recent history. markClosed is a no-op if the row was
    // already dropped, so a late SessionClosed after a reload is harmless.
    const { activeSessionId } = get();
    get().markClosed(notification.sessionId);
    // ADR-019 defensive clear — a closed conversation cannot have a live
    // draft awaiting review; without this a stale card lingers forever if
    // the EmailReplyDraftResolved event is ever lost.
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.sessionId === notification.sessionId ? { ...c, pendingDraft: undefined } : c,
      ),
    }));
    // If the just-closed session was the open thread, deselect it (the panel
    // returns to the empty state); the row remains in the Closed folder.
    if (activeSessionId === notification.sessionId) {
      set({ activeSessionId: null, messages: [] });
    }
  },

  setConversationCategory: (sessionId, category) => {
    touchRow(sessionId);
    // ADR-017 D2 — a pure classification change. Update ONLY the Category so the
    // ConversationList re-groups the row into its new directory section; every
    // other field (including P2's routing flags assignedToOther /
    // consultationTransactionId and the lifecycle state) is preserved via
    // spread. Re-sort so the row keeps its activity position in its section.
    set((state) => ({
      conversations: sortByActivity(
        state.conversations.map((c) =>
          c.sessionId === sessionId ? { ...c, category } : c,
        ),
      ),
    }));
  },

  setConversationPriority: (sessionId, priority, pinned) => {
    touchRow(sessionId);
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.sessionId === sessionId ? { ...c, priority, priorityPinned: pinned ?? c.priorityPinned } : c,
      ),
    }));
  },

  handleSessionCategoryChanged: (notification) => {
    // Delegates so the SignalR echo and the console's own optimistic write cannot drift apart.
    get().setConversationCategory(notification.sessionId, notification.category);
  },

  setConversationSubject: (sessionId, subject) => {
    touchRow(sessionId);
    // Deliberately NOT wrapped in sortByActivity: unlike handleSessionCategoryChanged, which
    // re-sorts because the row changes directory section, a rename changes nothing about where the
    // row belongs. Re-sorting here would shuffle the list under the agent mid-edit.
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.sessionId === sessionId ? { ...c, subject } : c,
      ),
    }));
  },

  markClosed: (sessionId) => {
    touchRow(sessionId);
    get().setBotThinking(sessionId, false);
    set((state) => ({
      conversations: sortByActivity(
        state.conversations.map((c) =>
          c.sessionId === sessionId ? { ...c, state: 'CLOSED' as SessionState } : c,
        ),
      ),
    }));
  },

  handleOutboundMessage: (notification) => {
    // OP-1005 - nothing legitimate is sent into a conversation marked as spam; a late outbound push
    // must not re-open its transcript or toggle its composing flag.
    if (isBlocked(get(), notification.sessionId)) return;

    // Convert the backend sender type ("ai" or "agent") into the UI enum.
    const sender =
      notification.senderType?.toLowerCase() === 'agent'
        ? ('AGENT' as const)
        : ('AI' as const);

    const msg: Message = {
      messageId: notification.messageId,
      sessionId: notification.sessionId,
      sender,
      senderId: notification.senderId,
      content: notification.content,
      contentType: notification.contentType || 'text',
      timestamp: notification.timestamp,
      attachments: notification.attachments,
      stream: 'chat',
      // Operator 2026-11-02 - the bot's public name (the hub sends "AI" when it has none).
      ...(sender === 'AI' && notification.senderName && notification.senderName !== 'AI'
        ? { botName: notification.senderName }
        : {}),
    };

    // The reply has landed — whether it's the bot's own answer or a human
    // agent's, the "composing" wait is over.
    get().setBotThinking(notification.sessionId, false);

    const { activeSessionId } = get();
    // If this message belongs to the conversation currently open, append to
    // the thread — otherwise just update the list row. Outbound messages
    // never increment `unreadCount` (that's for customer messages only).
    if (msg.sessionId === activeSessionId) {
      set((state) => ({ messages: [...state.messages, msg] }));
    }
    set((state) => ({
      conversations: sortByActivity(
        state.conversations.map((c) =>
        c.sessionId === notification.sessionId
          ? {
              ...c,
              lastMessageContent: notification.content,
              lastMessageTimestamp: notification.timestamp,
              // The newest message is now the bot's or an agent's: the row's preview line says who.
              lastPreview: previewText(notification.content),
              lastPreviewAt: notification.timestamp,
              lastPreviewSender: (sender === 'AGENT' ? 'agent' : 'bot') as 'agent' | 'bot',
              lastPreviewAgentName: sender === 'AGENT' ? notification.senderName?.trim() || null : null,
              // Server parity: an outbound (bot or agent) message is activity AND the last outbound.
              lastActivityAt: laterIso(c.lastActivityAt, notification.timestamp),
              lastOutboundAt: laterIso(c.lastOutboundAt, notification.timestamp),
              // An outbound (bot/agent) reply also counts as activity — bumps a
              // message-less row (e.g. a proactive first reply) into visibility.
              messageCount: c.messageCount ? c.messageCount : 1,
            }
          : c
        ),
      ),
    }));
  },
  handleOutboundDeliveryFailed: (notification) => {
    // Mark the exact bubble. No insert: a failure for a message not in this thread means the agent is
    // looking elsewhere, and inventing a message to hang a marker on would be worse than silence.
    set((state) => ({
      messages: state.messages.map((m) =>
        m.messageId === notification.messageId
          ? { ...m, deliveryFailed: true, deliveryFailureReason: notification.reason }
          : m
      ),
    }));
  },
  hydrateTimeline: (sessionId, entries) => {
    if (!sessionId) return;
    set((state) => {
      // OP-1005 - the timeline is not a way back into a conversation marked as spam.
      if (isBlocked(state, sessionId)) return state;
      const merged = mergeTimelineEntries(state.timeline[sessionId] ?? [], entries);
      // 2026-10-28 - the page also says WHY the bot last consulted the team (CustomerKeyword = "asked for a person"). The
      // page is HISTORY, so it decorates only a row that is consulting now: an old episode's reason must not sit on an
      // idle row and then label the next, different consultation before that one's own entry arrives.
      const row = state.conversations.find((c) => c.sessionId === sessionId);
      const conversations =
        row?.state === 'CONSULTATION_REQUESTED'
          ? withConsultationReason(state.conversations, sessionId, latestReasonOf(entries))
          : state.conversations;
      return { timeline: { ...state.timeline, [sessionId]: merged }, conversations };
    });
  },

  handleTimelineEntry: (notification) => {
    const entry = timelineEntryFromNotification(notification);
    if (!entry) return;
    const gate = get();
    const { activeSessionId } = gate;
    if (isBlocked(gate, entry.sessionId)) return;
    // 2026-10-28 - a consultation request's reason is recorded on the ROW for ANY conversation this console holds (the
    // list's "asks for a person" badge needs it for rows that are not open). Only the reason: the entry itself is still
    // kept for the open conversation alone, below.
    const mark = reasonOfEntry(entry);
    if (mark) {
      set((state) => {
        const conversations = withConsultationReason(state.conversations, entry.sessionId, mark);
        return conversations === state.conversations ? state : { conversations };
      });
    }
    if (activeSessionId !== entry.sessionId) return;
    set((state) => {
      const existing = state.timeline[entry.sessionId] ?? [];
      // Best-effort push vs authoritative reload: the same entry commonly arrives by both. Keep the copy
      // already held and do not touch state, so a duplicate causes no re-render.
      if (existing.some((e) => e.entryId === entry.entryId)) return state;
      return { timeline: { ...state.timeline, [entry.sessionId]: mergeTimelineEntries(existing, [entry]) } };
    });
  },
}));

/**
 * The rows to show for one folder (ADR-033 D13), from the shared row cache:
 *  - `drafts` is NOT a list folder (OP-582): every row with a pending draft, the longest-waiting first;
 *  - `search:<q>` is the server's answer in the server's order (the server already matched the text);
 *  - any other folder: the rows its pages listed PLUS live extras - cached rows the pages did not list that match the
 *    folder and are not older than the oldest row loaded so far (so a push-created or push-moved row appears at once, and
 *    an old row that merely sits in the cache does not butt into the middle of an unfinished page) - all of them filtered
 *    by `matcher` and the blocked gate, then ordered by activity. The server stays authoritative: the next page or reload
 *    corrects any extra the client could not judge, and the COUNTS always come from the server.
 *
 * `matcher` is the folder rule (C2-4's `folderMatcher(folder, ctx)`); it is a parameter so this selector has no opinion
 * on who the viewer is. Rows synthesised from a push (`partial`) are never extras: they are not known well enough.
 */
export function selectFolderRows(
  state: Pick<ConversationState, 'conversations' | 'folderPages' | 'spamTombstones' | 'hiddenSessions'>,
  folder: FolderId,
  matcher: (c: Conversation) => boolean,
  /** The page-cache key of the folder under the viewer's lifecycle tab (`folderPageKey`); defaults to the folder id (tab All). */
  pageKey: string = folder,
): Conversation[] {
  const byId = new Map(state.conversations.map((c) => [c.sessionId, c]));

  if (folder === 'drafts') {
    // The drafts FEED is the server's word on which drafts this viewer may see, so a row whose owner is merely unknown
    // (a placeholder not yet re-read) stays; a row whose owner is KNOWN is still judged by the viewer's visibility
    // (`matcher` = folderMatcher('drafts'), which applies canSee only to rows that name their owner), so a draft on a
    // conversation that has since turned out to be somebody else's does not linger in this folder.
    return state.conversations
      .filter((c) => !!c.pendingDraft && !isBlocked(state, c.sessionId) && matcher(c))
      .sort((a, b) => {
        const d = Date.parse(a.pendingDraft!.createdAtUtc) - Date.parse(b.pendingDraft!.createdAtUtc);
        if (Number.isFinite(d) && d !== 0) return d;
        return a.sessionId < b.sessionId ? -1 : a.sessionId > b.sessionId ? 1 : 0;
      });
  }

  const page = state.folderPages[pageKey];
  const listed = (page?.ids ?? [])
    .map((id) => byId.get(id))
    .filter((c): c is Conversation => !!c && !isBlocked(state, c.sessionId));

  if (folder.startsWith('search:')) return listed;

  const inPage = new Set(page?.ids ?? []);
  // Only an UNFINISHED page has a "below the fold" to protect; a fully loaded folder has no older unloaded rows.
  const floor = page && page.hasMore && listed.length > 0 ? Math.min(...listed.map(activityMs)) : -Infinity;
  const extras = state.conversations.filter(
    (c) => !inPage.has(c.sessionId) && !c.partial && !isBlocked(state, c.sessionId) && activityMs(c) >= floor,
  );

  return sortByActivity([...listed, ...extras].filter(matcher));
}

/**
 * ADR-033 - everything above is per tenant+agent: the hidden set, the row cache, the counts, the pending claims. The
 * signed-in user going away (logout, an expired session) or changing to a different tenant or agent wipes all of it, so
 * one person's hidden conversations (and their conversations) can never carry over to the next person on the same
 * browser. Signing IN from nothing wipes nothing (there is nothing of anyone else's to wipe, and the shell is still
 * mounting).
 */
const scopeOf = (user: { tenantId?: string; agentId?: string } | null | undefined): string | null =>
  user ? `${user.tenantId ?? ''}|${user.agentId ?? ''}` : null;

let boundScope = scopeOf(useAuthStore.getState().user);
useAuthStore.subscribe((auth) => {
  const next = scopeOf(auth.user);
  if (next === boundScope) return;
  const hadScope = boundScope !== null;
  boundScope = next;
  if (hadScope) useConversationStore.getState().resetAll();
});
